import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import {
  LayoutDashboard, ShoppingCart, Package, Users, Wallet, Layers,
  Sun, Moon, Globe, LogOut, Plus, Search, Printer, Truck, DollarSign,
  X, CheckCircle2, Send, Trash2, Pencil, Filter, FileDown, ChevronRight, ChevronDown, ChevronLeft,
  AlertTriangle, TrendingUp, Boxes, Receipt, Settings, MessageCircle,
  Sparkles, Grid, Ruler, FileText, Factory, Upload, Bell, CreditCard, ClipboardList, RefreshCw,
  Home, ChevronsLeft, ChevronsRight, Mail, Share2, Eye, EyeOff, Check, CalendarDays, Camera, FileSignature, Target, MapPin, Tag, Palette, AlertCircle, Download,
  Brain, Zap, Cpu, Info, Building2, History, Phone, Shield, BarChart2, Bot, Radio, Copy, Barcode, PlusCircle, ShieldAlert, ExternalLink, Loader2, Image as ImageIcon, GripVertical,
} from "lucide-react";
import { DndContext, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import { toast } from "sonner";
import api from "../api";
import { AuthCtx, UICtx, StockSettingsCtx } from "../appContexts";
import { fmt, fmtDate, fmtDT, ono, MONEY, convertPrice, STOR_KUMAS_GROUPS, ZEBRA_KUMAS_GROUPS, ZIP_KUMAS_GROUPS, FABRIC_GROUP_LABELS, RAL_COLORS } from "../appUtils";
import { slatSwatchStyle } from "../utils/pdfUtils";
import { translations, CURRENCY_SYMBOL, STATUS_LABELS } from "../mock";
import { makeTr } from "../i18n";
import { ModuleIcon, PName, MODULE_GRADIENTS } from "../components/layout/Sidebar";
import ZenPagination from "../components/ZenPagination";

const MOTOR_FORM_SLUGS = ["somfy", "nice", "mosel", "becker", "aok", "novo"];

const KARTELA_KINDS = [
  { key: "hierarchy", label: "Hiyerarşi" },
  { key: "fabric", label: "Kumaş" },
  { key: "ral", label: "RAL / Renk" },
  { key: "ral_zip", label: "ZIP RAL Paleti" },
  { key: "kasa_rengi", label: "Kasa Rengi" },
  { key: "wood", label: "Ahşap (AJ)" },
  { key: "wood_type", label: "Ahşap Çeşidi" },
  { key: "metal_slat", label: "Metal Slat" },
  { key: "mekanizma", label: "Mekanizma / Oluk" },
  { key: "motor", label: "Motor" },
  { key: "control", label: "Kumanda" },
  { key: "form_links", label: "Form Bağlantıları" },
];

// Kumaş ve Ahşap Çeşidi sekmelerinde ürün tipine göre alt kategori (kartela.group alanına göre filtre)
// "Diğer" her zaman: listelenen grupların dışında kalan kayıtları gösterir
const KARTELA_CATEGORY_MAP = {
  fabric: [
    { key: "stor", label: "Stor Perde", groups: ["stor", ...STOR_KUMAS_GROUPS], sub: [
        // Panama
        { key: "stor_panama",           label: "Panama" },
        // BlackOut grup başlığı + alt gruplar
        { key: "stor_blackout",         label: "BlackOut", isHeader: true },
        { key: "stor_blackout_genel",   label: "· Genel" },
        { key: "stor_blackout_fc_ithal",label: "· FC İthal" },
        { key: "stor_blackout_dokuma",  label: "· Dokuma" },
        { key: "stor_blackout_oxford",  label: "· Oxford" },
        { key: "stor_blackout_doku",    label: "· Doku" },
        { key: "stor_blackout_nubuk",   label: "· Nubuk" },
        { key: "stor_blackout_yaldizli",label: "· Yaldızlı" },
        { key: "stor_blackout_yaldizli_panama", label: "· Yaldızlı Panama" },
        { key: "stor_blackout_screen",  label: "· Screen BO" },
        { key: "stor_blackout_liyosel", label: "· Liyosel" },
        { key: "stor_blackout_fr",      label: "· FR Alevalmaz" },
        { key: "stor_blackout_panama",  label: "· Panama BO" },
        // Screen grup başlığı + alt gruplar
        { key: "stor_screen",           label: "Screen", isHeader: true },
        { key: "stor_screen_pes",       label: "· Pes" },
        { key: "stor_screen_polyester", label: "· Polyester" },
        { key: "stor_screen_silver",    label: "· Silver" },
        { key: "stor_screen_sun",       label: "· Sun Screen" },
        { key: "stor_screen_sunscreen1",label: "· Sunscreen 1" },
        { key: "stor_screen_sunscreen2",label: "· Sunscreen 2" },
        { key: "stor_screen_sunscreen3",label: "· Sunscreen 3" },
        { key: "stor_screen_sunscreen4",label: "· Sunscreen 4" },
        // Polyester
        { key: "stor_polyester",        label: "Polyester", isHeader: true },
        { key: "stor_polyester_mat",    label: "· Mat" },
        { key: "stor_polyester_super",  label: "· Süper" },
        // Doğal
        { key: "stor_dogal",            label: "Doğal / Vual", isHeader: true },
        { key: "stor_dogal_vual",       label: "· Vual" },
        { key: "stor_dogal_liyosel",    label: "· Liyosel" },
        { key: "stor_dogal_linen",      label: "· Mat Linen" },
        // Yaldızlı
        { key: "stor_yaldizli",         label: "Yaldızlı", isHeader: true },
        { key: "stor_yaldizli_pastel",  label: "· Pastel" },
        { key: "stor_yaldizli_panama",  label: "· Panama" },
        // Sandy & Sinevizyon
        { key: "stor_sandy",            label: "Sandy" },
        { key: "stor_sinevizyon",       label: "Sinevizyon" },
        // Sınıflandırılmamış (eski)
        { key: "stor",                  label: "Sınıflandırılmamış" },
      ] },
    { key: "zebra", label: "Zebra Perde", groups: ["zebra", ...ZEBRA_KUMAS_GROUPS] },
    { key: "tul_fon", label: "Tül & Fon", groups: ["tul_fon_perde", "tf_fon", "tf_tul", "tf_havuz"], sub: [
        { key: "tf_tul", label: "Tül" },
        { key: "tf_fon", label: "Fon Perde" },
        { key: "tf_havuz", label: "Havuz" },
      ] },
    { key: "ciftli_sistem", label: "Çiftli Sistem", groups: ["ciftli_sistem"] },
    { key: "dosemelik", label: "Döşemelik", groups: ["dosemelik"] },
    { key: "silhouette", label: "Silhouette", groups: ["silhouette"] },
    { key: "plise", label: "Plise Perde", groups: ["plise14", "plise25", "natty_plise", "honeycomb_16", "honeycomb_20", "honeycomb_26"], sub: [
        { key: "plise14", label: "PLİCELL 15mm" },
        { key: "plise25", label: "PLİCELL 20mm" },
        { key: "natty_plise", label: "Natty Plise" },
        { key: "honeycomb_16", label: "Honeycomb 16mm" },
        { key: "honeycomb_20", label: "Honeycomb 20mm" },
        { key: "honeycomb_26", label: "Honeycomb 26mm" },
      ] },
    { key: "zip", label: "Zip Perde", groups: ZIP_KUMAS_GROUPS, sub: [
        { key: "zip_kumas_86", label: "Serge Ferrari 86" },
        { key: "zip_kumas_92", label: "Serge Ferrari 92" },
        { key: "zip_kumas_96", label: "Serge Ferrari 96" },
        { key: "zip_kumas_helio", label: "Helioscreen" },
        { key: "zip_kumas_akrilik", label: "Akrilik" },
      ] },
    { key: "dikey", label: "Dikey Perde", groups: ["dikey", "dikey_kumas", "dikey_tul", "dikey_pvc"], sub: [
        { key: "dikey_kumas", label: "Kumaş" },
        { key: "dikey_tul", label: "Tül" },
        { key: "dikey_pvc", label: "PVC" },
      ] },
    { key: "other", label: "Diğer", groups: null },
  ],
  wood_type: [
    { key: "ahsap_jaluzi_25", label: "25mm", groups: ["ahsap_jaluzi_25"] },
    { key: "ahsap_jaluzi_35", label: "35mm", groups: ["ahsap_jaluzi_35"] },
    { key: "ahsap_jaluzi_50", label: "50mm", groups: ["ahsap_jaluzi_50"] },
    { key: "other", label: "Diğer", groups: null },
  ],
  wood: [
    { key: "ahsap_jaluzi_25", label: "25mm", groups: ["ahsap_jaluzi_25"] },
    { key: "ahsap_jaluzi_35", label: "35mm", groups: ["ahsap_jaluzi_35"] },
    { key: "ahsap_jaluzi_50", label: "50mm", groups: ["ahsap_jaluzi_50"] },
    { key: "evax_50", label: "Evax 50mm", groups: ["evax_50"] },
    { key: "other", label: "Diğer", groups: null },
  ],
  metal_slat: [
    { key: "metal_16", label: "16mm", groups: ["metal_16"] },
    { key: "metal_25", label: "25mm", groups: ["metal_25"] },
    { key: "metal_50", label: "50mm", groups: ["metal_50"] },
    { key: "other", label: "Diğer", groups: null },
  ],
};

// ─── ZIP RAL Renk Paleti ─────────────────────────────────────────────────────
const RAL_GROUPS = [
  { label: "Sarı",       range: [1000, 1999], accent: "#F3DA0B" },
  { label: "Turuncu",    range: [2000, 2999], accent: "#ED760E" },
  { label: "Kırmızı",   range: [3000, 3999], accent: "#AF2B1E" },
  { label: "Mor",        range: [4000, 4999], accent: "#924E7D" },
  { label: "Mavi",       range: [5000, 5999], accent: "#2271B3" },
  { label: "Yeşil",      range: [6000, 6999], accent: "#317F43" },
  { label: "Gri",        range: [7000, 7999], accent: "#7D7F7D" },
  { label: "Kahve",      range: [8000, 8999], accent: "#6F4F28" },
  { label: "Beyaz/Siyah",range: [9000, 9999], accent: "#404040" },
];

function RalSwatchPalette() {
  const [q, setQ] = useState("");
  const [group, setGroup] = useState(null);
  const filtered = RAL_COLORS.filter(r => {
    const num = parseInt(r.code.replace("RAL ", ""), 10);
    if (group) {
      const g = RAL_GROUPS.find(x => x.label === group);
      if (g && (num < g.range[0] || num > g.range[1])) return false;
    }
    if (q) {
      const lo = q.toLowerCase();
      return r.code.toLowerCase().includes(lo) || r.name.toLowerCase().includes(lo);
    }
    return true;
  });
  return (
    <div className="space-y-4 zen-anim-up">
      {/* Başlık + arama */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-[#131B2E] border border-slate-200 dark:border-[#22304A] text-sm">
          <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="RAL kodu veya renk ara…"
            className="bg-transparent outline-none w-44 placeholder-slate-400 text-slate-800 dark:text-slate-100 text-sm"
            data-testid="ral-palette-search"
          />
          {q && <button onClick={() => setQ("")}><X className="w-3.5 h-3.5 text-slate-400" /></button>}
        </div>
        <span className="text-[11px] text-slate-400">{filtered.length} renk</span>
        <div className="flex-1" />
      </div>

      {/* Grup filtreleri */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setGroup(null)}
          className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${!group ? "bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 border-slate-800 dark:border-slate-200" : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}
        >Tümü ({RAL_COLORS.length})</button>
        {RAL_GROUPS.map(g => {
          const cnt = RAL_COLORS.filter(r => { const n = parseInt(r.code.replace("RAL ",""),10); return n>=g.range[0]&&n<=g.range[1]; }).length;
          return (
            <button key={g.label}
              onClick={() => setGroup(g.label === group ? null : g.label)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${group===g.label ? "text-white border-transparent" : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}
              style={group===g.label ? {background:g.accent} : {}}
            >
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{background:g.accent}} />
              {g.label} ({cnt})
            </button>
          );
        })}
      </div>

      {/* Renk kartı grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2">
        {filtered.map(r => (
          <div key={r.code} className="zen-panel p-0 overflow-hidden group cursor-default" title={`${r.code} — ${r.name}`}
            data-testid={`ral-swatch-${r.code.replace(/\s/g,"-")}`}>
            <div className="w-full aspect-square" style={{ background: r.hex }} />
            <div className="p-1.5">
              <div className="text-[10px] font-black font-mono text-slate-700 dark:text-slate-200 leading-tight">{r.code}</div>
              <div className="text-[9px] text-slate-500 dark:text-slate-400 leading-tight truncate">{r.name}</div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-400 text-sm">Eşleşen renk bulunamadı</div>
        )}
      </div>
    </div>
  );
}

// ── Form Bağlantıları: Kumaş Grubu ↔ Form Eşleştirme ────────────────────────
const KNOWN_FABRIC_GROUPS_FOR_LINKS = [
  { key: "plise14", label: "PLİCELL 15mm", cat: "Plise" },
  { key: "plise25", label: "PLİCELL 20mm", cat: "Plise" },
  { key: "natty_plise", label: "Natty Plise", cat: "Plise" },
  { key: "honeycomb_16", label: "Honeycomb 16mm", cat: "Plise" },
  { key: "honeycomb_20", label: "Honeycomb 20mm", cat: "Plise" },
  { key: "honeycomb_26", label: "Honeycomb 26mm", cat: "Plise" },
  { key: "silhouette", label: "Silhouette", cat: "Silhouette" },
  { key: "stor", label: "Stor (Genel)", cat: "Stor" },
  { key: "stor_polyester", label: "Stor Polyester", cat: "Stor" },
  { key: "stor_panama", label: "Stor Panama", cat: "Stor" },
  { key: "stor_yaldizli", label: "Stor Yaldızlı", cat: "Stor" },
  { key: "stor_dogal", label: "Stor Doğal", cat: "Stor" },
  { key: "stor_screen", label: "Stor Screen", cat: "Stor" },
  { key: "stor_blackout", label: "Stor Blackout", cat: "Stor" },
  { key: "stor_ozel", label: "Stor Özel", cat: "Stor" },
  { key: "zip_kumas_86", label: "Zip Serge Ferrari 86", cat: "Zip" },
  { key: "zip_kumas_92", label: "Zip Serge Ferrari 92", cat: "Zip" },
  { key: "zip_kumas_96", label: "Zip Serge Ferrari 96", cat: "Zip" },
  { key: "zip_kumas_helio", label: "Zip Helioscreen", cat: "Zip" },
  { key: "zip_kumas_akrilik", label: "Zip Akrilik", cat: "Zip" },
  { key: "ciftli_sistem", label: "Çiftli Sistem", cat: "Diğer" },
  { key: "dikey_kumas", label: "Dikey Kumaş", cat: "Dikey" },
  { key: "dikey_pvc", label: "Dikey PVC", cat: "Dikey" },
  { key: "dikey_tul", label: "Dikey Tül", cat: "Dikey" },
];

function FormLinksSection({ productGroups }) {
  const [localMap, setLocalMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [dragGroup, setDragGroup] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/form-fabric-links");
      const known = new Set(KNOWN_FABRIC_GROUPS_FOR_LINKS.map(g => g.key));
      // Sadece bilinen kumaş gruplarını göster (motor/marka grupları da aynı alanı kullanıyor, onları hariç tut)
      const filtered = {};
      Object.entries(data || {}).forEach(([formSlug, groups]) => {
        const kept = (groups || []).filter(g => known.has(g));
        if (kept.length > 0) filtered[formSlug] = kept;
      });
      setLocalMap(filtered);
    } catch { toast.error("Form bağlantıları yüklenemedi"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const applyLink = async (formSlug, groupKey, action) => {
    const key = `${formSlug}:${groupKey}`;
    setBusyKey(key);
    // Optimistic güncelleme — anında yansısın
    setLocalMap(prev => {
      const cur = prev[formSlug] || [];
      const next = action === "link" ? [...new Set([...cur, groupKey])] : cur.filter(g => g !== groupKey);
      return { ...prev, [formSlug]: next };
    });
    try {
      await api.post("/form-fabric-links", { group: groupKey, formSlug, action });
      toast.success(action === "link" ? "Bağlandı — Sipariş Formu ve Kartela'ya anında yansıdı" : "Bağlantı kaldırıldı");
    } catch {
      toast.error("Kaydedilemedi");
      loadLinks();
    } finally {
      setBusyKey(null);
    }
  };

  const handleDrop = (formSlug) => {
    if (!dragGroup) return;
    applyLink(formSlug, dragGroup, "link");
    setDragGroup(null); setDragOver(null);
  };

  const removeLink = (formSlug, groupKey) => applyLink(formSlug, groupKey, "unlink");

  const getLabel = (key) => KNOWN_FABRIC_GROUPS_FOR_LINKS.find(g => g.key === key)?.label || key;

  const fabricGroupsByCat = KNOWN_FABRIC_GROUPS_FOR_LINKS.reduce((acc, g) => {
    (acc[g.cat] = acc[g.cat] || []).push(g);
    return acc;
  }, {});

  return (
    <div className="zen-anim-up space-y-4">
      <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-400/30 text-xs text-emerald-700 dark:text-emerald-300" data-testid="ffg-live-notice">
        ✓ Bu ekran canlıdır: bağladığınız her kumaş grubu <b>anında</b> veritabanına ({`assets.groupSlugs`}) yazılır ve
        Sipariş Formu ile Kartela Hiyerarşisi'nde aynı anda görünür (tek veri kaynağı).
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-800 dark:text-white">Kumaş Grubu ↔ Form Bağlantıları</h3>
          <p className="text-xs text-slate-500 mt-0.5">Kumaş grubunu forma sürükleyin — o form açıldığında bu kumaşlar anında görünür.</p>
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </div>

      <div className="grid grid-cols-[260px_1fr] gap-5">
        {/* Sol: Kumaş Grupları (sürüklenebilir) */}
        <div className="space-y-4">
          {Object.entries(fabricGroupsByCat).map(([cat, groups]) => (
            <div key={cat}>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">{cat}</div>
              <div className="space-y-1">
                {groups.map(fg => {
                  const usedIn = Object.entries(localMap)
                    .filter(([, grps]) => grps.includes(fg.key))
                    .map(([slug]) => productGroups.find(g => g.slug === slug)?.name || slug);
                  return (
                    <div key={fg.key} draggable data-testid={`ffg-drag-${fg.key}`}
                      onDragStart={() => setDragGroup(fg.key)}
                      onDragEnd={() => { setDragGroup(null); setDragOver(null); }}
                      className={`px-3 py-1.5 rounded-lg border cursor-grab active:cursor-grabbing select-none transition-all flex items-start gap-2
                        ${dragGroup === fg.key
                          ? "bg-[#E5322A]/10 border-[#E5322A] scale-95 shadow-sm"
                          : "bg-white dark:bg-[#0C1220] border-slate-200 dark:border-[#22304A] hover:border-slate-400 dark:hover:border-[#3A4A6A]"}`}>
                      <Tag className="w-3 h-3 mt-0.5 text-slate-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{fg.label}</div>
                        {usedIn.length > 0 && <div className="text-[10px] text-slate-400 truncate">{usedIn.join(", ")}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Sağ: Ürün Formları (bırakma hedefleri) */}
        <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 content-start">
          {productGroups.map(form => {
            const linked = localMap[form.slug] || [];
            const isTarget = dragGroup !== null;
            const isOver = dragOver === form.slug;
            return (
              <div key={form.id} data-testid={`ffg-drop-${form.slug}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(form.slug); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={() => handleDrop(form.slug)}
                className={`rounded-xl border p-3 min-h-[90px] transition-all
                  ${isOver
                    ? "border-[#E5322A] bg-[#E5322A]/5 dark:bg-[#E5322A]/10 shadow-md"
                    : isTarget
                      ? "border-dashed border-slate-300 dark:border-[#2A3A5A]"
                      : "border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0C1220]"}`}>
                <div className="text-[11px] font-bold text-slate-700 dark:text-slate-100 mb-1.5">{form.name}</div>
                {linked.length === 0 ? (
                  <div className="text-[10px] text-slate-400 italic">
                    {isTarget ? "Buraya bırakın..." : "Bu formun kendi kumaş grubu dışında bağlantısı yok"}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {linked.map(gKey => (
                      <span key={gKey} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 dark:bg-[#131B2E] text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-[#22304A]">
                        {busyKey === `${form.slug}:${gKey}` && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
                        {getLabel(gKey)}
                        <button onClick={() => removeLink(form.slug, gKey)} data-testid={`ffg-remove-${form.slug}-${gKey}`}
                          className="text-slate-400 hover:text-rose-500 transition-colors">
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Hiyerarşi Yardımcı Modal ────────────────────────────────────────────────
function HierarchySeriesModal({ series, formSlug, parentId, onClose, onSave }) {
  const { theme } = useContext(UICtx);
  const dark = theme === "dark";
  const [form, setForm] = useState(series || { name: "", code: "", stockStatus: "in_stock", groupSlugs: [] });
  const [saving, setSaving] = useState(false);
  const [productGroups, setProductGroups] = useState([]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/product-groups").then(r => setProductGroups((r.data || []).filter(g => !MOTOR_FORM_SLUGS.includes(g.slug) && g.active !== false))).catch(() => {});
  }, []);

  const toggleSlug = (slug) => {
    const cur = form.groupSlugs || [];
    setForm(p => ({ ...p, groupSlugs: cur.includes(slug) ? cur.filter(s => s !== slug) : [...cur, slug] }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Seri adı gerekli"); return; }
    setSaving(true);
    try {
      if (series) {
        const { data } = await api.put(`/fabric-series/${series.id}`, form);
        onSave(data, "update");
      } else {
        const { data } = await api.post("/fabric-series", { ...form, formSlug, parentId: parentId || null });
        onSave(data, "create");
      }
      toast.success(series ? "Güncellendi" : "Seri eklendi");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
    finally { setSaving(false); }
  };

  const fabricForms = productGroups.filter(pg => !MOTOR_FORM_SLUGS.includes(pg.slug) && pg.active !== false && pg.slug !== formSlug);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="hier-series-modal">
      <div className="bg-white dark:bg-[#131B2E] rounded-2xl shadow-2xl w-full max-w-md p-5 border border-slate-200 dark:border-[#22304A]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-slate-800 dark:text-white">{series ? "Seri Düzenle" : "Yeni Seri / Grup"}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Seri Adı *</label>
            <input className="zen-input" placeholder="örn: Blackout" value={form.name}
              onChange={e => set("name", e.target.value)} autoFocus data-testid="hier-series-name" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Grup Kodu</label>
            <input className="zen-input font-mono text-xs" placeholder="örn: stor_blackout" value={form.code || ""}
              onChange={e => set("code", e.target.value)} data-testid="hier-series-code" />
            <p className="text-[10px] text-slate-400 mt-1">Sipariş formunda bu gruptan kumaşlar gösterilir</p>
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Stok Durumu</label>
            <select className="zen-input" value={form.stockStatus || "in_stock"} onChange={e => set("stockStatus", e.target.value)}>
              <option value="in_stock">Stokta</option>
              <option value="out_of_stock">Stok Dışı</option>
            </select>
          </div>

          {/* Çoklu Form Atama */}
          {fabricForms.length > 0 && (
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                Diğer Formlarda da Görünsün
                <span className="ml-1 font-normal normal-case text-slate-500">(opsiyonel)</span>
              </label>
              <div className="flex flex-wrap gap-1.5" data-testid="hier-series-group-slugs">
                {fabricForms.map(pg => {
                  const checked = (form.groupSlugs || []).includes(pg.slug);
                  return (
                    <button key={pg.slug} type="button" onClick={() => toggleSlug(pg.slug)}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all
                        ${checked ? "bg-[#E5322A] text-white border-[#E5322A] shadow-sm" : `border-slate-200 dark:border-white/10 text-slate-500 hover:bg-slate-50 dark:hover:bg-white/5`}`}
                      data-testid={`hier-series-slug-${pg.slug}`}>
                      {checked && <Check className="w-3 h-3 shrink-0" />}
                      {pg.name}
                    </button>
                  );
                })}
              </div>
              {(form.groupSlugs || []).length > 0 ? (
                <p className="text-[10px] text-[#E5322A] mt-1 font-semibold">
                  {(form.groupSlugs || []).length} ek formda görünecek
                </p>
              ) : (
                <p className="text-[10px] text-slate-400 mt-1">Seçim yapılmadı — yalnızca "{formSlug}" formunda görünür</p>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 zen-btn-ghost text-sm">İptal</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 zen-btn-primary text-sm flex items-center justify-center gap-1.5 disabled:opacity-50"
            data-testid="hier-series-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Taşı / Kopyala Popup ────────────────────────────────────────────────────
function MoveCopyDialog({ asset, targetSeries, onMove, onCopy, onClose }) {
  const [saving, setSaving] = useState(false);
  const handle = async (action) => {
    setSaving(true);
    try { await action(); }
    catch (e) { toast.error(e?.response?.data?.detail || "İşlem hatası"); setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[990] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-[#131B2E] rounded-2xl shadow-2xl border border-slate-200 dark:border-[#22304A] p-5 w-80 zen-anim-up" onClick={e => e.stopPropagation()}>
        <div className="text-center mb-4">
          <div className="w-10 h-10 rounded-full bg-[#E5322A]/10 flex items-center justify-center mx-auto mb-3">
            <GripVertical className="w-5 h-5 text-[#E5322A]" />
          </div>
          <div className="text-sm font-bold text-slate-800 dark:text-white mb-1 leading-snug">
            "{asset.label}"
          </div>
          <div className="text-xs text-slate-500">
            → <span className="font-semibold text-slate-700 dark:text-slate-300">"{targetSeries.name}"</span> serisine
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => handle(onMove)} disabled={saving}
            className="py-3 rounded-xl bg-slate-100 dark:bg-[#0C1220] hover:bg-[#E5322A] hover:text-white text-slate-700 dark:text-slate-200 text-sm font-semibold transition-all flex flex-col items-center justify-center gap-1.5 disabled:opacity-50 border border-transparent hover:border-[#E5322A]"
            data-testid="dnd-move-btn">
            <ChevronRight className="w-4 h-4" />
            Taşı
          </button>
          <button onClick={() => handle(onCopy)} disabled={saving}
            className="py-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-600 hover:text-white text-blue-700 dark:text-blue-300 text-sm font-semibold transition-all flex flex-col items-center justify-center gap-1.5 disabled:opacity-50 border border-blue-100 dark:border-blue-900/40 hover:border-blue-600"
            data-testid="dnd-copy-btn">
            <Copy className="w-4 h-4" />
            Kopyala
          </button>
        </div>
        <button onClick={onClose} className="mt-3 w-full text-xs text-slate-400 hover:text-slate-600 py-1.5 transition-colors rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
          İptal
        </button>
      </div>
    </div>
  );
}

// ─── Sürüklenebilir Varlık Satırı ────────────────────────────────────────────
function DraggableAssetItem({ a, dark, isMotorForm, isAdmin, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `asset-${a.id}`,
    data: { asset: a },
  });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 rounded-xl px-3 py-2 transition-colors relative select-none
        ${isDragging ? "opacity-40 scale-95 z-50 ring-2 ring-[#E5322A]/40 shadow-lg" : ""}
        ${dark ? "hover:bg-white/5" : "hover:bg-slate-50"}`}
      data-testid={`hier-asset-${a.id}`}
    >
      {isAdmin && (
        <button {...attributes} {...listeners}
          className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 shrink-0 touch-none p-0.5"
          title="Farklı seriye taşımak için sürükleyin"
          data-testid={`hier-asset-drag-${a.id}`}
          onClick={e => e.stopPropagation()}
        >
          <GripVertical className="w-3 h-3" />
        </button>
      )}
      {a.imageBase64 ? (
        <img src={a.imageBase64} alt={a.label} className="w-8 h-8 rounded-lg object-cover shrink-0 border border-slate-200 dark:border-white/10 shadow-sm" />
      ) : a.hexColor ? (
        <div className="w-8 h-8 rounded-lg shrink-0 border border-slate-200 dark:border-white/10 shadow-sm" style={{ background: a.hexColor }} />
      ) : isMotorForm ? (
        <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center shrink-0 border border-amber-200 dark:border-amber-900/40">
          <Zap className="w-3.5 h-3.5 text-amber-500" />
        </div>
      ) : (
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${dark ? "bg-white/5 border-white/10" : "bg-slate-100 border-slate-200"}`}>
          <Palette className="w-3 h-3 text-slate-400" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold truncate text-slate-800 dark:text-slate-100">{a.label}</div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {a.code && <span className="text-[9px] font-mono text-slate-400">{a.code}{a.codeEnd && a.codeEnd !== a.code ? `–${a.codeEnd}` : ""}</span>}
          {a.price > 0 && <span className="text-[9px] text-[#E5322A] font-semibold">{a.price} {a.currency}</span>}
          {a.outOfStock && <span className="text-[8px] font-black bg-rose-500 text-white px-1 rounded">STOK DIŞI</span>}
        </div>
      </div>
      {isAdmin && (
        <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onEdit(a)}
            className={`p-1.5 rounded-lg transition-all text-slate-400 hover:text-slate-700 dark:hover:text-slate-100 hover:bg-slate-200 dark:hover:bg-white/10`}
            data-testid={`hier-asset-edit-${a.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(a)}
            className={`p-1.5 rounded-lg transition-all text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30`}
            data-testid={`hier-asset-delete-${a.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Bırakma Hedefi: Seri Öğesi ──────────────────────────────────────────────
function DroppableSeriesBtn({ s, isActive, dark, isAdmin, onSelect, onEdit, onDelete, itemBaseCls, itemActiveCls }) {
  const { isOver, setNodeRef } = useDroppable({ id: `series-${s.id}`, data: { series: s } });
  return (
    <div ref={setNodeRef} className="relative">
      <button onClick={onSelect}
        className={`${itemBaseCls} ${isActive ? itemActiveCls + " border" : ""} transition-all
          ${isOver ? "ring-2 ring-[#E5322A]/60 !bg-[#E5322A]/10 !border-[#E5322A]/40" : ""}`}
        data-testid={`hier-series-${s.id}`}>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className={`w-2 h-2 rounded-full shrink-0 ${s.stockStatus === "in_stock" ? "bg-emerald-500" : "bg-slate-400"}`} />
          <div className="text-left min-w-0 flex-1">
            <div className="text-xs font-semibold truncate">{s.name}</div>
            {s.code && <div className="text-[9px] font-mono text-slate-400">{s.code}</div>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {s.assetCount > 0 && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${dark ? "bg-white/10 text-slate-400" : "bg-slate-100 text-slate-500"}`}>
              {s.assetCount}
            </span>
          )}
          {(s.groupSlugs || []).length > 0 && (
            <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold bg-[#E5322A]/15 text-[#E5322A] border border-[#E5322A]/20" title={`${(s.groupSlugs||[]).length} formda daha görünür`}>
              +{(s.groupSlugs || []).length}
            </span>
          )}
          {isAdmin && (
            <div className="hidden group-hover:flex items-center gap-0.5">
              <button onClick={e => { e.stopPropagation(); onEdit(s); }}
                className={`p-1 rounded transition-colors ${dark ? "hover:bg-white/10" : "hover:bg-slate-100"} text-slate-400`}>
                <Pencil className="w-3 h-3" />
              </button>
              <button onClick={e => { e.stopPropagation(); onDelete(s); }}
                className={`p-1 rounded transition-colors ${dark ? "hover:bg-rose-900/30" : "hover:bg-rose-50"} text-rose-400`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          )}
          <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? "rotate-90 text-[#E5322A]" : "text-slate-300"}`} />
        </div>
      </button>
      {isOver && (
        <div className="absolute inset-0 rounded-xl pointer-events-none border-2 border-dashed border-[#E5322A]/50" />
      )}
    </div>
  );
}

// ─── Hiyerarşi Ana Paneli ─────────────────────────────────────────────────────
function HierarchyPanel() {
  const { user } = useContext(AuthCtx);
  const { theme } = useContext(UICtx);
  const isAdmin = user?.role === "admin";
  const dark = theme === "dark";

  const [productForms, setProductForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [series, setSeries] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [subSeries, setSubSeries] = useState([]);
  const [selectedSub, setSelectedSub] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [seeding, setSeeding] = useState(false);

  // Marka filtresi (admin only)
  const [activeBrand, setActiveBrand] = useState("__all__");
  const [editingFormSlug, setEditingFormSlug] = useState(null);
  const [brandInputVal, setBrandInputVal] = useState("");
  const [savingBrand, setSavingBrand] = useState(false);

  // Modaller
  const [showSeriesModal, setShowSeriesModal] = useState(false);
  const [editSeries, setEditSeries] = useState(null);
  const [showSubModal, setShowSubModal] = useState(false);
  const [editSub, setEditSub] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);

  // Drag-and-Drop state
  const [dragPayload, setDragPayload] = useState(null); // { asset, targetSeries }

  const allBrands = useMemo(() =>
    [...new Set(productForms.map(f => f.brand || "").filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")),
    [productForms]
  );

  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/product-forms");
      setProductForms(data || []);
    } catch { toast.error("Ürün formları yüklenemedi"); }
    finally { setLoading(false); }
  }, []);

  const loadSeries = useCallback(async (slug) => {
    try {
      const { data } = await api.get(`/fabric-series?formSlug=${slug}&parentId=root`);
      setSeries(data || []);
    } catch { setSeries([]); }
  }, []);

  const loadSubSeries = useCallback(async (parentId) => {
    if (!parentId) { setSubSeries([]); return; }
    try {
      const { data } = await api.get(`/fabric-series?parentId=${parentId}`);
      setSubSeries(data || []);
    } catch { setSubSeries([]); }
  }, []);

  const loadAssets = useCallback(async (groupCode, seriesName) => {
    if (!groupCode) { setAssets([]); return; }
    setAssetsLoading(true);
    try {
      let url = `/assets?group=${encodeURIComponent(groupCode)}`;
      if (seriesName) url += `&seriesName=${encodeURIComponent(seriesName)}`;
      let { data } = await api.get(url);
      // seriesName eşleşmesi yoksa (ör. seriesName henüz etiketlenmemiş serilerde) tüm gruba düş
      if (seriesName && (!data || data.length === 0)) {
        ({ data } = await api.get(`/assets?group=${encodeURIComponent(groupCode)}`));
      }
      setAssets(data || []);
    } catch { setAssets([]); }
    finally { setAssetsLoading(false); }
  }, []);

  useEffect(() => { loadForms(); }, [loadForms]);

  const handleSelectForm = (form) => {
    setSelectedForm(form);
    setSelectedSeries(null);
    setSelectedSub(null);
    setSubSeries([]);
    setAssets([]);
    setSeries([]);
    loadSeries(form.slug);
  };

  const handleSelectSeries = (s) => {
    setSelectedSeries(s);
    setSelectedSub(null);
    setAssets([]);
    loadSubSeries(s.id);
    if (s.code) loadAssets(s.code);
  };

  const handleSelectSub = (sub) => {
    setSelectedSub(sub);
    loadAssets(sub.code || selectedSeries?.code, sub.assetCode);
  };

  const handleSaveBrand = async () => {
    if (!editingFormSlug) return;
    setSavingBrand(true);
    try {
      await api.put(`/product-forms/${editingFormSlug}`, { brand: brandInputVal.trim() });
      setProductForms(prev => prev.map(f =>
        f.slug === editingFormSlug ? { ...f, brand: brandInputVal.trim() } : f
      ));
      toast.success("Marka güncellendi");
    } catch { toast.error("Kaydedilemedi"); }
    finally { setSavingBrand(false); setEditingFormSlug(null); }
  };

  const handleDeleteAsset = async (a) => {
    if (!window.confirm(`"${a.label}" arşive taşınsın mı?`)) return;
    try {
      await api.delete(`/assets/${a.id}`);
      setAssets(p => p.filter(x => x.id !== a.id));
      toast.success(`"${a.label}" arşive taşındı`);
    } catch { toast.error("Silinemedi"); }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const asset = active.data.current?.asset;
    const targetSeries = over.data.current?.series;
    if (asset && targetSeries && targetSeries.code !== (asset.group || "")) {
      setDragPayload({ asset, targetSeries });
    }
  };

  const handleDeleteSeries = async (s) => {
    if (!window.confirm(`"${s.name}" serisini silmek istiyor musunuz?`)) return;
    try {
      await api.delete(`/fabric-series/${s.id}`);
      setSeries(p => p.filter(x => x.id !== s.id));
      if (selectedSeries?.id === s.id) { setSelectedSeries(null); setSubSeries([]); setAssets([]); }
      toast.success("Seri silindi");
    } catch { toast.error("Silinemedi"); }
  };

  const handleDeleteSub = async (s) => {
    if (!window.confirm(`"${s.name}" alt serisini silmek istiyor musunuz?`)) return;
    try {
      await api.delete(`/fabric-series/${s.id}`);
      setSubSeries(p => p.filter(x => x.id !== s.id));
      if (selectedSub?.id === s.id) { setSelectedSub(null); setAssets([]); }
      toast.success("Alt seri silindi");
    } catch { toast.error("Silinemedi"); }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/fabric-series/seed");
      const parts = [`${data.pf_created} form`, `${data.fs_created} seri`];
      if (data.asset_created > 0) parts.push(`${data.asset_created} motor modeli`);
      toast.success(`Seed tamamlandı: ${parts.join(", ")} eklendi`);
      await loadForms();
      if (selectedForm) loadSeries(selectedForm.slug);
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed hatası"); }
    finally { setSeeding(false); }
  };

  const filteredForms = useMemo(() => {
    if (activeBrand === "__all__") return productForms;
    if (activeBrand === "__none__") return productForms.filter(f => !f.brand);
    return productForms.filter(f => (f.brand || "") === activeBrand);
  }, [productForms, activeBrand]);

  const filteredAssets = assets.filter(a =>
    !search ||
    (a.label || "").toLowerCase().includes(search.toLowerCase()) ||
    (a.code || "").toLowerCase().includes(search.toLowerCase())
  );

  const isMotorForm = MOTOR_FORM_SLUGS.includes(selectedForm?.slug);

  const panelCls = `rounded-2xl border flex flex-col overflow-hidden h-full shadow-lg transition-all
    ${dark
      ? "bg-slate-800/60 border-slate-700/40 backdrop-blur-md"
      : "bg-white border-slate-100 shadow-sm"}`;
  const headerCls = `flex items-center justify-between px-4 py-3 border-b text-[10px] font-bold uppercase tracking-wider
    ${dark
      ? "border-slate-700/30 text-slate-400 bg-slate-900/40"
      : "border-slate-100 text-slate-500 bg-slate-50/80"}`;
  const itemActiveCls = dark ? "!border-[#E5322A]/50 bg-[#E5322A]/10 text-[#E5322A]" : "!border-[#E5322A]/30 bg-rose-50 text-[#E5322A]";
  const itemBaseCls = `group w-full flex items-center justify-between px-3 py-2.5 rounded-xl border transition-all cursor-pointer text-left
    ${dark
      ? "border-white/5 hover:border-white/15 hover:bg-white/5 text-slate-200"
      : "border-slate-100 hover:border-slate-200 hover:bg-slate-50 text-slate-700"}`;

  if (loading) return (
    <div className="flex items-center justify-center h-64 gap-2 text-slate-400">
      <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
    </div>
  );

  return (
    <div className="space-y-3" data-testid="hierarchy-panel">
      {/* Marka Filtre Satırı (sadece admin) */}
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 self-center shrink-0">MARKA</span>
          {[
            { key: "__all__", label: `Tümü (${productForms.length})` },
            ...allBrands.map(b => ({ key: b, label: `${b} (${productForms.filter(f => f.brand === b).length})` })),
            { key: "__none__", label: `Markasız (${productForms.filter(f => !f.brand).length})` },
          ].map(({ key, label }) => (
            <button key={key}
              onClick={() => setActiveBrand(key === activeBrand && key !== "__all__" ? "__all__" : key)}
              className={`px-3 py-1.5 rounded-full text-[10px] font-semibold border transition-all ${activeBrand === key ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A] text-slate-500 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}
              data-testid={`hier-brand-filter-${key}`}>
              {label}
            </button>
          ))}
          <div className="flex-1" />
          <button onClick={handleSeed} disabled={seeding}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-semibold transition-all disabled:opacity-50 ${dark ? "border-[#22304A] text-slate-400 hover:bg-[#131B2E]" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}
            title="Eksik seri/form verilerini ekle (idempotent)" data-testid="hier-seed-btn">
            {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {seeding ? "Yükleniyor..." : "Seed"}
          </button>
        </div>
      )}

      {/* 3-Sütun Hiyerarşi Grid — DnD sarmalayıcı */}
      <DndContext onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-12 gap-3" style={{ height: "calc(100vh - 265px)", minHeight: "480px" }}>

        {/* PANEL 1 — ÜRÜN FORMLARI (Kumaş + Motor ayrımı) */}
        <div className={`col-span-4 ${panelCls}`} data-testid="hier-panel-forms">
          <div className={headerCls}>
            <span className="flex items-center gap-1.5"><Package className="w-3 h-3" /> Ürün Formu</span>
            <span className="font-normal text-slate-400">{filteredForms.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {filteredForms.length === 0 ? (
              <div className="text-center py-10">
                <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-xs text-slate-400 mb-2">Form bulunamadı</p>
                {isAdmin && <button onClick={handleSeed} className="text-xs text-[#E5322A] font-semibold hover:underline">Seed ile Yükle</button>}
              </div>
            ) : (() => {
              const fabricForms = filteredForms.filter(f => !MOTOR_FORM_SLUGS.includes(f.slug));
              const motorForms  = filteredForms.filter(f =>  MOTOR_FORM_SLUGS.includes(f.slug));
              const renderForm = (form) => (
                <div key={form.slug} onClick={() => handleSelectForm(form)}
                  className={`${itemBaseCls} ${selectedForm?.slug === form.slug ? itemActiveCls + " border" : ""}`}
                  data-testid={`hier-form-${form.slug}`}>
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${MOTOR_FORM_SLUGS.includes(form.slug) ? "bg-amber-400" : form.isActive !== false ? "bg-emerald-500" : "bg-slate-300"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold truncate">{form.name}</div>
                      {isAdmin && (
                        editingFormSlug === form.slug ? (
                          <div className="flex items-center gap-1 mt-0.5" onClick={e => e.stopPropagation()}>
                            <input
                              className={`text-[10px] rounded px-1.5 py-0.5 outline-none border w-28 ${dark ? "bg-black/30 border-white/20 text-slate-200" : "bg-white border-slate-300 text-slate-700"} focus:ring-1 focus:ring-[#E5322A]`}
                              value={brandInputVal} onChange={e => setBrandInputVal(e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") handleSaveBrand(); if (e.key === "Escape") setEditingFormSlug(null); }}
                              placeholder="Marka adı..." autoFocus data-testid={`hier-brand-input-${form.slug}`} />
                            <button onClick={handleSaveBrand} disabled={savingBrand} className="text-emerald-500 disabled:opacity-50" data-testid={`hier-brand-save-${form.slug}`}>
                              {savingBrand ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            </button>
                            <button onClick={() => setEditingFormSlug(null)} className="text-rose-400"><X className="w-3 h-3" /></button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 group/brand mt-0.5">
                            {form.brand && <span className="text-[10px] text-[#E5322A] font-semibold">{form.brand}</span>}
                            <button onClick={e => { e.stopPropagation(); setEditingFormSlug(form.slug); setBrandInputVal(form.brand || ""); }}
                              className="opacity-0 group-hover/brand:opacity-100 transition-opacity text-slate-400 hover:text-slate-600" data-testid={`hier-brand-edit-${form.slug}`}>
                              <Pencil className="w-2.5 h-2.5" />
                            </button>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${selectedForm?.slug === form.slug ? "rotate-90 text-[#E5322A]" : "text-slate-300"}`} />
                </div>
              );
              return (
                <>
                  {fabricForms.map(renderForm)}
                  {motorForms.length > 0 && (
                    <>
                      <div className={`flex items-center gap-2 px-2 py-2 mt-1 border-t ${dark ? "border-white/10" : "border-slate-100"}`}>
                        <Zap className="w-3 h-3 text-amber-500 shrink-0" />
                        <span className="text-[9px] font-bold uppercase tracking-widest text-amber-500">Motor Markaları</span>
                      </div>
                      {motorForms.map(renderForm)}
                    </>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* PANEL 2 — SERİLER (DroppableSeriesBtn ile) */}
        <div className={`col-span-4 ${panelCls}`} data-testid="hier-panel-series">
          <div className={headerCls}>
            <span className="flex items-center gap-1.5">
              {isMotorForm ? <Zap className="w-3 h-3 text-amber-400" /> : <Tag className="w-3 h-3" />}
              {selectedForm ? selectedForm.name : "Seri / Grup"}
            </span>
            <div className="flex items-center gap-1.5">
              <span className="font-normal text-slate-400">{series.length}</span>
              {isAdmin && selectedForm && (
                <button onClick={() => { setEditSeries(null); setShowSeriesModal(true); }}
                  className={`p-1 rounded-md text-white transition-colors ${isMotorForm ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-500 hover:bg-emerald-600"}`}
                  data-testid="hier-add-series-btn">
                  <Plus className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {!selectedForm ? (
              <div className="flex flex-col items-center justify-center h-32 text-slate-400 gap-2">
                <ChevronRight className="w-6 h-6 text-slate-300" />
                <span className="text-xs">Ürün formu seçin</span>
              </div>
            ) : series.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Tag className="w-6 h-6 text-slate-300 mx-auto mb-1" />
                <p className="text-xs mb-1">Seri bulunamadı</p>
                {isAdmin && <button onClick={() => { setEditSeries(null); setShowSeriesModal(true); }} className="text-xs text-emerald-500 font-semibold hover:underline">+ Seri Ekle</button>}
              </div>
            ) : series.map(s => (
              <DroppableSeriesBtn
                key={s.id}
                s={s}
                isActive={selectedSeries?.id === s.id}
                dark={dark}
                isAdmin={isAdmin}
                onSelect={() => handleSelectSeries(s)}
                onEdit={(s) => { setEditSeries(s); setShowSeriesModal(true); }}
                onDelete={handleDeleteSeries}
                itemBaseCls={itemBaseCls}
                itemActiveCls={itemActiveCls}
              />
            ))}
          </div>
        </div>

        {/* PANEL 3 — RENKLER / VARYANTLAR (DraggableAssetItem ile) */}
        <div className={`col-span-4 ${panelCls}`} data-testid="hier-panel-assets">
          <div className={headerCls}>
            <span className="flex items-center gap-1.5">
              {isMotorForm ? <Zap className="w-3 h-3 text-amber-400" /> : <Palette className="w-3 h-3" />}
              {selectedSeries ? selectedSeries.name : (isMotorForm ? "Motor Modeli" : "Renk / Varyant")}
            </span>
            <div className="flex items-center gap-1.5">
              <span className="font-normal text-slate-400">{assets.length}</span>
              {isAdmin && (selectedSeries || selectedSub) && (
                <button
                  onClick={() => setEditingAsset({ group: selectedSub?.code || selectedSeries?.code, kind: isMotorForm ? "motor" : "fabric" })}
                  className={`p-1 rounded-md text-white transition-colors ${isMotorForm ? "bg-amber-500 hover:bg-amber-600" : "bg-[#E5322A] hover:bg-[#c42a22]"}`}
                  data-testid="hier-add-asset-btn">
                  <Plus className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Alt seri chip'leri */}
          {subSeries.length > 0 && (
            <div className={`flex flex-wrap gap-1 px-3 py-2 border-b ${dark ? "border-white/10" : "border-slate-100"}`}>
              {subSeries.map(sub => (
                <button key={sub.id} onClick={() => handleSelectSub(sub)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${selectedSub?.id === sub.id ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-white/10 text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5"}`}
                  data-testid={`hier-sub-${sub.id}`}>
                  {sub.name}
                </button>
              ))}
              {isAdmin && (
                <button onClick={() => { setEditSub(null); setShowSubModal(true); }}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border border-dashed transition-all ${dark ? "border-white/10 text-slate-500 hover:border-emerald-500 hover:text-emerald-400" : "border-slate-300 text-slate-400 hover:border-emerald-400 hover:text-emerald-500"}`}
                  data-testid="hier-add-sub-btn">+ Alt Grup</button>
              )}
            </div>
          )}

          {/* Arama */}
          {assets.length > 5 && (
            <div className={`flex items-center gap-2 px-3 py-2 border-b ${dark ? "border-white/10" : "border-slate-100"}`}>
              <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <input className="bg-transparent text-xs outline-none flex-1 text-slate-700 dark:text-slate-200 placeholder-slate-400"
                placeholder={isMotorForm ? "Motor ara..." : "Renk ara..."}
                value={search} onChange={e => setSearch(e.target.value)} data-testid="hier-asset-search" />
              {search && <button onClick={() => setSearch("")} className="text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /></button>}
            </div>
          )}

          {/* Motor Form Atama Bilgisi */}
          {isMotorForm && selectedSeries && (
            <div className={`px-3 py-1.5 border-b text-[10px] flex items-center gap-1.5 ${dark ? "border-white/10 text-amber-400/70" : "border-amber-100 text-amber-600"}`}>
              <Info className="w-3 h-3 shrink-0" />
              Motor modellerini düzenleyerek hangi ürün formlarında görüneceğini seçebilirsiniz
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-3">
            {!selectedSeries ? (
              <div className="flex flex-col items-center justify-center h-32 text-slate-400 gap-2">
                {isMotorForm ? <Zap className="w-6 h-6 text-amber-300" /> : <Palette className="w-6 h-6 text-slate-300" />}
                <span className="text-xs">Seri seçin</span>
              </div>
            ) : assetsLoading ? (
              <div className="flex items-center justify-center h-24 gap-1 text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : filteredAssets.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                {isMotorForm ? <Zap className="w-6 h-6 text-amber-300 mx-auto mb-1" /> : <Palette className="w-6 h-6 text-slate-300 mx-auto mb-1" />}
                <p className="text-xs mb-2">Kayıt bulunamadı</p>
                {isAdmin && (selectedSeries || selectedSub) && (
                  <button onClick={() => setEditingAsset({ group: selectedSub?.code || selectedSeries?.code, kind: isMotorForm ? "motor" : "fabric" })}
                    className="text-xs text-[#E5322A] font-semibold hover:underline">+ Ekle</button>
                )}
              </div>
            ) : (
              <div className="space-y-0.5">
                {filteredAssets.map(a => (
                  <DraggableAssetItem
                    key={a.id}
                    a={a}
                    dark={dark}
                    isMotorForm={isMotorForm}
                    isAdmin={isAdmin}
                    onEdit={(a) => setEditingAsset(a)}
                    onDelete={handleDeleteAsset}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      </DndContext>

      {/* ── Modaller ── */}
      {showSeriesModal && (
        <HierarchySeriesModal
          series={editSeries}
          formSlug={selectedForm?.slug}
          parentId={null}
          onClose={() => { setShowSeriesModal(false); setEditSeries(null); }}
          onSave={(data, type) => {
            if (type === "create") setSeries(p => [...p, data]);
            else setSeries(p => p.map(s => s.id === data.id ? data : s));
          }}
        />
      )}
      {showSubModal && (
        <HierarchySeriesModal
          series={editSub}
          formSlug={selectedForm?.slug}
          parentId={selectedSeries?.id}
          onClose={() => { setShowSubModal(false); setEditSub(null); }}
          onSave={(data, type) => {
            if (type === "create") setSubSeries(p => [...p, data]);
            else setSubSeries(p => p.map(s => s.id === data.id ? data : s));
          }}
        />
      )}
      {/* Varlık düzenleme — KartelaForm aracılığıyla (resim yükleme dahil) */}
      {editingAsset && (
        <KartelaForm
          kind={editingAsset.kind || (isMotorForm ? "motor" : "fabric")}
          data={editingAsset}
          brands={allBrands}
          onClose={() => setEditingAsset(null)}
          onSave={async (fd) => {
            if (!fd.label?.trim()) { toast.error("İsim gerekli"); return; }
            try {
              if (editingAsset?.id) {
                await api.put(`/assets/${editingAsset.id}`, fd);
              } else {
                await api.post("/assets", { ...fd });
              }
              toast.success("Kaydedildi");
              setEditingAsset(null);
              loadAssets(selectedSub?.code || selectedSeries?.code);
            } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt hatası"); }
          }}
          stockEnabled={false}
        />
      )}

      {/* Taşı / Kopyala Popup */}
      {dragPayload && (
        <MoveCopyDialog
          asset={dragPayload.asset}
          targetSeries={dragPayload.targetSeries}
          onMove={async () => {
            await api.put(`/assets/${dragPayload.asset.id}`, { ...dragPayload.asset, group: dragPayload.targetSeries.code });
            toast.success(`"${dragPayload.asset.label}" taşındı → ${dragPayload.targetSeries.name}`);
            setDragPayload(null);
            loadAssets(selectedSub?.code || selectedSeries?.code);
          }}
          onCopy={async () => {
            const { id, ...rest } = dragPayload.asset;
            await api.post("/assets", { ...rest, group: dragPayload.targetSeries.code });
            toast.success(`"${dragPayload.asset.label}" kopyalandı → ${dragPayload.targetSeries.name}`);
            setDragPayload(null);
          }}
          onClose={() => setDragPayload(null)}
        />
      )}
    </div>
  );
}

function KartelaModule() {
  const { tr } = useContext(UICtx);
  const [kind, setKind] = useState("hierarchy");
  const stock = useContext(StockSettingsCtx);
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [activeSub, setActiveSub] = useState(null);
  const [assetSearch, setAssetSearch] = useState("");
  const [activeBrand, setActiveBrand] = useState(null);
  const [onlyOos, setOnlyOos] = useState(false);
  const [gridPage, setGridPage] = useState(1);
  const [gridPageSize, setGridPageSize] = useState(30);
  const [showArchive, setShowArchive] = useState(false);
  const [archivedRows, setArchivedRows] = useState([]);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [rates, setRates] = useState({ eurToTl: 38, usdToTl: 34 });
  useEffect(() => { api.get("/rates").then(r => setRates(r.data)).catch(() => {}); }, []);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const loadRequestRef = useRef(0);

  // Form Bağlantıları için
  const [productGroups, setProductGroups] = useState([]);
  useEffect(() => {
    api.get("/product-groups").then(r => setProductGroups((r.data || []).filter(g => g.active !== false))).catch(() => {});
  }, []);
  const load = async () => {
    const requestId = ++loadRequestRef.current;
    setLoadingRows(true);
    try {
      const r = await api.get(`/assets?kind=${kind}`);
      if (requestId === loadRequestRef.current) setRows(r.data);
    } finally {
      if (requestId === loadRequestRef.current) setLoadingRows(false);
    }
  };
  useEffect(() => { setActiveCat(null); setActiveSub(null); setAssetSearch(""); setActiveBrand(null); setShowArchive(false); load(); }, [kind]);

  const loadArchive = async () => {
    setLoadingArchive(true);
    try {
      const r = await api.get(`/assets?kind=${kind}&archived=true`);
      setArchivedRows(r.data);
    } finally {
      setLoadingArchive(false);
    }
  };

  const toggleArchive = () => {
    const next = !showArchive;
    setShowArchive(next);
    if (next) loadArchive();
  };

  const save = async (data) => {
    if (!data.label?.trim()) { toast.error("İsim / Etiket gerekli"); return; }
    if (editing?.id) await api.put(`/assets/${editing.id}`, data);
    else await api.post("/assets", { ...data, kind });
    toast.success("Kaydedildi"); setEditing(null); load();
  };
  const del = async (a) => {
    if (!window.confirm(`"${a.label}" arşive taşınsın mı?\n(Silinmez — Silinenler Arşivi'nden geri getirebilirsiniz)`)) return;
    await api.delete(`/assets/${a.id}`);
    toast.success(`"${a.label}" arşive taşındı`, { description: "Silinenler Arşivi'nden geri getirebilirsiniz" });
    load();
  };

  const restore = async (a) => {
    await api.post(`/assets/${a.id}/restore`);
    toast.success(`"${a.label}" geri getirildi`);
    loadArchive();
    load();
  };

  // Hızlı grup değiştir (Tül/Fon için inline kategori)
  const quickSetGroup = async (a, newGroup) => {
    await api.put(`/assets/${a.id}`, { ...a, group: newGroup });
    setRows(prev => prev.map(r => r.id === a.id ? { ...r, group: newGroup } : r));
  };

  // Tüm atanmamış tül/fon kumaşlarına otomatik kategori ata
  const autoDetectTulFonCategories = async () => {
    const unassigned = visibleRows.filter(r => !["tf_tul","tf_fon","dosemelik"].includes(r.group));
    if (unassigned.length === 0) { toast.info("Tüm kumaşlar zaten kategorize edilmiş"); return; }
    const det = (r) => {
      if (Number(r.fabricWidthCm) === 140) return "dosemelik";
      const n = (r.label || "").toLocaleLowerCase("tr");
      if (/tül|tul/.test(n)) return "tf_tul";
      return "tf_fon";
    };
    await Promise.all(unassigned.map(r => api.put(`/assets/${r.id}`, { ...r, group: det(r) })));
    toast.success(`${unassigned.length} kumaş otomatik kategorize edildi`);
    load();
  };

  // Kumaş/Ahşap Çeşidi için kartela.group alanına göre alt kategori filtresi
  const catConfig = KARTELA_CATEGORY_MAP[kind] || null;
  const taggedGroups = catConfig ? catConfig.flatMap((c) => c.groups || (c.sub ? c.sub.map((s) => s.key) : [])) : [];
  const activeCatDef = catConfig?.find((c) => c.key === activeCat);
  const countInGroups = (groups) => rows.filter((r) => groups.includes(r.group)).length;
  const countOther = () => rows.filter((r) => !taggedGroups.includes(r.group)).length;
  const baseRows = !catConfig || !activeCatDef
    ? rows
    : activeCatDef.groups === null
      ? rows.filter((r) => !taggedGroups.includes(r.group))
      : activeCatDef.sub && activeSub
        ? rows.filter((r) => r.group === activeSub)
        : rows.filter((r) => activeCatDef.groups.includes(r.group));
  const brandChips = Object.entries(
    baseRows.reduce((acc, r) => { const k = r.brand || "Markasız"; acc[k] = (acc[k] || 0) + 1; return acc; }, {})
  ).sort((a, b) => a[0].localeCompare(b[0], "tr"));
  const brandRows = activeBrand ? baseRows.filter((r) => (r.brand || "Markasız") === activeBrand) : baseRows;
  const searchQ = assetSearch.trim().toLowerCase();
  const visibleRows = (searchQ
      ? brandRows.filter((r) => `${r.label || ""} ${r.code || ""}`.toLowerCase().includes(searchQ))
      : brandRows)
    .filter((r) => !onlyOos || r.outOfStock);
  // PERFORMANS: grid sınırsız render yerine sayfalanır (büyük kartelalarda DOM şişmesini önler)
  const pagedAssets = visibleRows.slice((gridPage - 1) * gridPageSize, gridPage * gridPageSize);
  useEffect(() => { setGridPage(1); }, [assetSearch, kind, activeCat, activeSub, activeBrand, onlyOos]);
  const oosCount = rows.filter((r) => r.outOfStock).length;
  const allBrands = [...new Set(rows.map((r) => r.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr"));
  const defaultGroupForNew = activeCatDef ? (activeSub || activeCatDef.groups?.[0] || "") : "";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        {KARTELA_KINDS.map(k => (
          <button key={k.key} onClick={() => { if (k.key !== kind) { setRows([]); setKind(k.key); } }} data-testid={`kartela-tab-${k.key}`}
            className={`px-4 py-2 rounded-lg text-xs font-semibold border transition-all ${kind === k.key ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A] text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}>
            {tr(k.label)}{kind === k.key && kind !== "ral_zip" && kind !== "form_links" && kind !== "hierarchy" ? ` (${rows.length})` : ""}
          </button>
        ))}
        <div className="flex-1" />
        {/* Stok Dışı filtresi — tüm türler için */}
        {oosCount > 0 && !showArchive && kind !== "hierarchy" && (
          <button onClick={() => setOnlyOos(v => !v)} data-testid="kartela-oos-filter"
            className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition-all ${onlyOos ? "bg-rose-500 text-white border-rose-500" : "border-rose-500/40 text-rose-500 hover:bg-rose-500/10"}`}>
            {tr("Stok Dışı")} ({oosCount})
          </button>
        )}
        {/* Silinenler Arşivi */}
        {kind !== "ral_zip" && kind !== "form_links" && kind !== "hierarchy" && (
          <button onClick={toggleArchive} data-testid="kartela-archive-toggle"
            className={`px-3 py-1.5 rounded-full text-[11px] font-bold border flex items-center gap-1.5 transition-all ${showArchive ? "bg-slate-700 text-white border-slate-700 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200" : "border-slate-300 dark:border-[#22304A] text-slate-500 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}>
            <History className="w-3.5 h-3.5" /> Arşiv {archivedRows.length > 0 ? `(${archivedRows.length})` : ""}
          </button>
        )}
        {/* Zip Perde kategorisinde toplu fotoğraf yükleme butonu */}
        {kind === "fabric" && activeCat === "zip" && (
          <button onClick={() => setBulkUploadOpen(true)} className="zen-btn-ghost text-xs flex items-center gap-1.5" data-testid="bulk-photo-btn">
            <Upload className="w-3.5 h-3.5" /> Toplu Fotoğraf Yükle
          </button>
        )}
        {/* Tül & Fon kategorisinde otomatik kategori algılama butonu */}
        {kind === "fabric" && activeCat === "tul_fon" && (
          <button onClick={autoDetectTulFonCategories} className="zen-btn-ghost text-xs flex items-center gap-1.5" data-testid="kartela-auto-cat-btn" title="Adı ve eni göre Tül/Fon/Döşemelik kategorilerini otomatik atar">
            <Zap className="w-3.5 h-3.5" /> Otomatik Kategori Algıla
          </button>
        )}
        {kind !== "ral_zip" && kind !== "form_links" && kind !== "hierarchy" && (
          <button onClick={() => setEditing({ group: defaultGroupForNew })} data-testid="kartela-new" className="zen-btn-primary"><Plus className="w-4 h-4" /> {tr("Yeni")} {tr(KARTELA_KINDS.find(k => k.key === kind)?.label || "")}</button>
        )}
      </div>

      {/* ── ZIP RAL Paleti Özel Görünüm ── */}
      {kind === "ral_zip" && <RalSwatchPalette />}

      {/* ── Form Bağlantıları ── */}
      {kind === "form_links" && (
        <FormLinksSection productGroups={productGroups} />
      )}

      {/* ── Hiyerarşi Görünümü ── */}
      {kind === "hierarchy" && <HierarchyPanel />}

      {/* ── Normal kartela görünümü ── */}
      {kind !== "ral_zip" && kind !== "form_links" && kind !== "hierarchy" && !showArchive && (<>
      {catConfig && (
        <div className="flex flex-wrap gap-1.5 pb-2 border-b border-slate-200 dark:border-[#22304A]">
          <button onClick={() => { setActiveCat(null); setActiveSub(null); setAssetSearch(""); setActiveBrand(null); }} data-testid={`kartela-cat-all`}
            className={`px-3 py-1.5 rounded-md text-[11px] font-semibold border transition-all ${!activeCat ? "bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 border-slate-800 dark:border-slate-200" : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}>
            {tr("Tümü")} ({rows.length})
          </button>
          {catConfig.map((c) => {
            const cnt = c.groups === null ? countOther() : countInGroups(c.groups);
            if (cnt === 0 && c.key === "other") return null; // "Diğer (0)" gösterme
            return (
              <button key={c.key} onClick={() => { setActiveCat(c.key); setActiveSub(null); setAssetSearch(""); setActiveBrand(null); }} data-testid={`kartela-cat-${c.key}`}
                className={`px-3 py-1.5 rounded-md text-[11px] font-semibold border transition-all ${activeCat === c.key ? "bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 border-slate-800 dark:border-slate-200" : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}>
                {tr(c.label)} ({cnt})
              </button>
            );
          })}
        </div>
      )}
      {catConfig && (
        <div className="relative mb-1" data-testid="kartela-search-wrap">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={assetSearch}
            onChange={(e) => setAssetSearch(e.target.value)}
            placeholder={`${activeCatDef ? tr(activeCatDef.label) : tr("Tümü")} içinde ara…`}
            data-testid="kartela-search-input"
            className="w-full pl-9 pr-9 py-2 rounded-xl text-[12px] font-medium bg-white/50 dark:bg-white/[0.04] backdrop-blur-md border border-white/50 dark:border-white/10 shadow-sm text-slate-700 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none transition-all duration-200 focus:bg-white/80 dark:focus:bg-white/[0.08] focus:border-[#E5322A]/50 focus:shadow-[0_0_0_3px_rgba(229,50,42,0.15)]"
          />
          {assetSearch && (
            <button onClick={() => setAssetSearch("")} data-testid="kartela-search-clear"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
      {activeCatDef?.sub && (
        <div className="flex flex-wrap gap-1.5 -mt-1 pb-2">
          <button onClick={() => { setActiveSub(null); setAssetSearch(""); setActiveBrand(null); }} data-testid="kartela-subcat-all"
            className={`px-2.5 py-1 rounded-md text-[10px] font-semibold border transition-all ${!activeSub ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A] text-slate-500 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}>
            Tümü
          </button>
          {activeCatDef.sub.map((s) => (
            s.isHeader ? (
              <span key={s.key} className="px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 self-center border-l-2 border-slate-300 dark:border-slate-600 ml-1 pl-2">
                {tr(s.label)}
              </span>
            ) : (
              <button key={s.key} onClick={() => { setActiveSub(s.key); setAssetSearch(""); setActiveBrand(null); }} data-testid={`kartela-subcat-${s.key}`}
                className={`px-2.5 py-1 rounded-md text-[10px] font-semibold border transition-all ${activeSub === s.key ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A] text-slate-500 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}>
                {tr(s.label)} ({countInGroups([s.key])})
              </button>
            )
          ))}
        </div>
      )}
      {brandChips.length > 1 && (
        <div className="flex flex-wrap gap-1.5 -mt-1 pb-2" data-testid="kartela-brand-chips">
          <span className="text-[10px] font-bold text-slate-400 self-center mr-1">{tr("MARKA")}</span>
          <button onClick={() => { setActiveBrand(null); setAssetSearch(""); }} data-testid="kartela-brand-all"
            className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${!activeBrand ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A] text-slate-500 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}>
            {tr("Tüm markalar")}
          </button>
          {brandChips.map(([b, n]) => (
            <button key={b} onClick={() => { setActiveBrand(b === activeBrand ? null : b); setAssetSearch(""); }} data-testid={`kartela-brand-chip-${b}`}
              className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${activeBrand === b ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A] text-slate-500 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}>
              {b} ({n})
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {pagedAssets.map(a => {
          // Görsel kaynağı: önce Object Storage, sonra base64
          const _tok = typeof localStorage !== "undefined" ? (localStorage.getItem("zen_token") || "") : "";
          const _apiUrl = process.env.REACT_APP_BACKEND_URL || "";
          const storageSrc = a.imageStoragePath
            ? `${_apiUrl}/api/files/${a.imageStoragePath}?auth=${encodeURIComponent(_tok)}`
            : null;
          const imgSrc = storageSrc || a.imageBase64 || null;
          const hasPhoto = !!imgSrc;

          return (
          <div key={a.id} className={`zen-panel flex flex-col group relative overflow-hidden ${hasPhoto ? "p-0" : "p-3 items-center text-center"}`} data-testid={`kartela-card-${a.code || a.id}`}>
            {hasPhoto ? (
              /* ── TAM FOTOĞRAF KARTI ── */
              <div className="relative cursor-zoom-in overflow-hidden" onClick={() => setPreviewAsset({ ...a, _imgSrc: imgSrc })}>
                <img
                  src={imgSrc}
                  alt={a.label}
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover transition-transform duration-300 group-hover:scale-125"
                  onError={e => { e.currentTarget.style.display="none"; }}
                />
                {/* Hover overlay — ad ve kod */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-end p-2">
                  <span className="text-white text-[11px] font-bold leading-tight line-clamp-2">{a.label}</span>
                  {a.code && <span className="text-white/70 text-[10px] font-mono">{a.code}</span>}
                  {a.price > 0 && <span className="text-[#FF7A6E] text-[10px] font-bold">{a.price} {a.currency}</span>}
                </div>
                {/* Stok uyarısı — sağ üst */}
                {a.outOfStock && (
                  <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[8px] font-black bg-rose-500 text-white shadow">STOK DIŞI</div>
                )}
                {kind === "fabric" && stock.enabled && (a.currentStock ?? 0) < stock.threshold && (
                  <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[8px] font-bold bg-amber-500 text-white shadow flex items-center gap-1">
                    <AlertTriangle className="w-2.5 h-2.5" /> {(a.currentStock ?? 0).toFixed(1)}m
                  </div>
                )}
                {/* Düzenle/Sil butonları */}
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={e => { e.stopPropagation(); setEditing(a); }} data-testid={`kartela-edit-${a.code || a.id}`}
                    className="p-1 bg-white/90 dark:bg-[#131B2E]/90 border border-slate-200 dark:border-[#22304A] rounded shadow-sm hover:bg-slate-100 dark:hover:bg-[#22304A] transition-colors">
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); del(a); }} data-testid={`kartela-delete-${a.code || a.id}`}
                    className="p-1 rounded shadow-sm transition-colors bg-rose-500/10 text-rose-500 hover:bg-rose-500/20 border border-rose-200 dark:border-rose-900">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ) : a.hexColor ? (
              /* ── HEX RENK SWATCHI ── */
              <div className="relative group/kcard">
                <div
                  onClick={() => setPreviewAsset(a)}
                  className="w-20 h-20 rounded-lg border-2 border-slate-200 dark:border-[#22304A] cursor-zoom-in hover:scale-105 hover:shadow-lg transition-all duration-150 shadow-sm"
                  style={slatSwatchStyle(a.hexColor, a.label)}
                />
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/kcard:flex flex-col items-center z-50 pointer-events-none">
                  <span className="bg-slate-900 text-white rounded-md px-2.5 py-1.5 shadow-xl whitespace-nowrap text-left">
                    <span className="block text-[11px] font-bold leading-tight">{a.label}</span>
                    {a.code && <span className="block text-[10px] text-slate-300 leading-tight">{a.code}</span>}
                    <span className="block text-[10px] text-slate-400 font-mono leading-tight">{a.hexColor}</span>
                  </span>
                  <span className="w-2 h-2 bg-slate-900 rotate-45 -mt-1" />
                </span>
              </div>
            ) : kind === "metal_slat" ? (
              <div onClick={() => setEditing(a)} className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 dark:border-[#31405D] cursor-pointer hover:border-[#E5322A]/60 bg-slate-100 dark:bg-[#1A2438] flex flex-col items-center justify-center gap-1 transition-colors" title="Renk kodu eklemek için tıklayın">
                <Palette className="w-5 h-5 text-slate-400" />
                <span className="text-[9px] text-slate-400 text-center px-1 leading-tight">{a.code || "Renk Ekle"}</span>
              </div>
            ) : (
              <div onClick={() => setEditing(a)} className="w-20 h-20 rounded-lg bg-slate-100 dark:bg-[#22304A] flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:bg-slate-200 dark:hover:bg-[#1E2D44] transition-colors gap-1" title="Fotoğraf eklemek için tıklayın">
                <Camera className="w-5 h-5" />
                <span className="text-[9px]">Fotoğraf Ekle</span>
              </div>
            )}

            {/* Alt bilgi — sadece fotoğraf olmayan kartlarda */}
            {!hasPhoto && (<>
              <div className="text-xs font-bold mt-2 truncate w-full">{a.label}</div>
              {a.code && <div className="text-[10px] text-slate-500 zen-mono">{a.code}{a.codeEnd ? `-${a.codeEnd}` : ""}</div>}
              {kind === "fabric" && activeCat === "tul_fon" && (() => {
                const CAT_OPTS = [
                  { val: "tf_tul", label: "Tül", color: "bg-indigo-500/15 text-indigo-600 border-indigo-500/40" },
                  { val: "tf_fon", label: "Fon", color: "bg-emerald-500/15 text-emerald-600 border-emerald-500/40" },
                  { val: "dosemelik", label: "Döş.", color: "bg-amber-500/15 text-amber-600 border-amber-500/40" },
                ];
                return (
                  <div className="flex gap-1 mt-1 w-full" onClick={e => e.stopPropagation()}>
                    {CAT_OPTS.map(opt => (
                      <button key={opt.val} type="button" data-testid={`quick-cat-${a.code || a.id}-${opt.val}`}
                        title={opt.val === "tf_tul" ? "Tül" : opt.val === "tf_fon" ? "Fon Perde" : "Döşemelik"}
                        onClick={() => quickSetGroup(a, opt.val)}
                        className={`flex-1 py-0.5 rounded text-[9px] font-bold border transition-all ${a.group === opt.val ? opt.color + " shadow-sm scale-105" : "border-slate-200 dark:border-[#22304A] text-slate-400 hover:border-slate-400 dark:hover:border-slate-500"}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                );
              })()}
              {a.brand && <div className="text-[10px] text-slate-400 truncate w-full" data-testid={`kartela-brand-${a.code || a.id}`}>{a.brand}</div>}
              {a.price > 0 && <div className="text-[11px] font-semibold text-[#E5322A]">{a.price} {a.currency}</div>}
              {a.purchasePrice > 0 && (() => {
                const costTL = convertPrice(a.purchasePrice, a.purchaseCurrency || "TL", "TL", rates);
                const saleTL = a.price > 0 ? convertPrice(a.price, a.currency || "EUR", "TL", rates) : 0;
                const pct = saleTL > 0 ? ((saleTL - costTL) / saleTL) * 100 : null;
                return (
                  <div className="text-[10px] leading-tight mt-0.5" data-testid={`kartela-cost-${a.code || a.id}`}>
                    <span className="text-slate-500">Alış ₺{costTL.toFixed(2)}</span>
                    {pct !== null && <span className={pct < 0 ? " text-rose-500 font-bold" : " text-emerald-600 dark:text-emerald-400 font-semibold"}> · %{pct.toFixed(0)} marj</span>}
                    {pct === null && <span className="text-amber-500 font-semibold"> · satış fiyatı yok</span>}
                  </div>
                );
              })()}
              {a.outOfStock && <div className="mt-1 px-2 py-0.5 rounded text-[9px] font-black bg-rose-500 text-white shadow-sm" data-testid={`kartela-oos-${a.code || a.id}`}>STOK DIŞI</div>}
              {kind === "fabric" && stock.enabled && (
                <div className={`mt-1 px-1.5 py-0.5 rounded text-[9px] font-bold border flex items-center gap-1 ${(a.currentStock ?? 0) < stock.threshold ? "bg-amber-500/15 text-amber-600 border-amber-500/30" : "bg-emerald-500/10 text-emerald-600 border-emerald-500/25"}`}
                  data-testid={`kartela-stock-${a.code || a.id}`}>
                  {(a.currentStock ?? 0) < stock.threshold && <AlertTriangle className="w-2.5 h-2.5" />}
                  Stok: {(a.currentStock ?? 0).toFixed(1)} m
                </div>
              )}
              <div className="absolute top-1 right-1 flex gap-1">
                <button onClick={() => setEditing(a)} data-testid={`kartela-edit-${a.code || a.id}`} className="p-1 bg-white/90 dark:bg-[#131B2E]/90 border border-slate-200 dark:border-[#22304A] rounded shadow-sm hover:bg-slate-100 dark:hover:bg-[#22304A] transition-colors opacity-0 group-hover:opacity-100">
                  <Pencil className="w-3 h-3" />
                </button>
                <button onClick={() => del(a)} data-testid={`kartela-delete-${a.code || a.id}`}
                  className={`p-1 rounded shadow-sm transition-colors ${a.outOfStock ? "opacity-100 bg-rose-500 text-white hover:bg-rose-600" : "opacity-0 group-hover:opacity-100 bg-rose-500/10 text-rose-500 hover:bg-rose-500/20 border border-rose-200 dark:border-rose-900"}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </>)}
          </div>
          );
        })}
      {loadingRows && visibleRows.length === 0 && <div className="col-span-full text-center text-slate-500 py-12 text-sm">Kartela yükleniyor…</div>}
      {!loadingRows && visibleRows.length === 0 && !showArchive && <div className="col-span-full text-center text-slate-500 py-12 text-sm" data-testid="kartela-empty-state">{kind === "metal_slat" && !activeCat ? "Henüz metal slat rengi yok." : "Bu kategoride kartela yok. Yeni ekleyin."}</div>}
      </div>
      {!showArchive && visibleRows.length > 0 && (
        <ZenPagination total={visibleRows.length} page={gridPage} pageSize={gridPageSize} onPage={setGridPage} onPageSize={(s) => { setGridPageSize(s); setGridPage(1); }} />
      )}

      {editing && <KartelaForm kind={kind} data={editing} brands={allBrands} onClose={() => setEditing(null)} onSave={save} stockEnabled={stock.enabled} />}
      {previewAsset && (
        <KartelaLightbox asset={previewAsset} onClose={() => setPreviewAsset(null)} />
      )}
      {bulkUploadOpen && (
        <BulkPhotoUpload
          rows={visibleRows}
          onClose={() => setBulkUploadOpen(false)}
          onSaved={load}
        />
      )}
      </>)}

      {/* ── Silinenler Arşivi ── */}
      {kind !== "ral_zip" && kind !== "hierarchy" && showArchive && (
        <div className="mt-4 space-y-3" data-testid="kartela-archive-view">
          <div className="flex items-center gap-3 px-1">
            <History className="w-4 h-4 text-slate-400" />
            <span className="font-bold text-slate-700 dark:text-slate-200 text-sm">Silinenler Arşivi</span>
            <span className="text-[11px] text-slate-400">— Buradan kolayca geri getirebilirsiniz</span>
            <button onClick={loadArchive} className="ml-auto zen-btn-ghost !text-xs !py-1" data-testid="kartela-archive-reload">
              <RefreshCw className="w-3 h-3" /> Yenile
            </button>
          </div>
          {loadingArchive && <div className="text-slate-400 text-sm text-center py-8">Arşiv yükleniyor…</div>}
          {!loadingArchive && archivedRows.length === 0 && (
            <div className="text-slate-400 text-sm text-center py-8 italic" data-testid="kartela-archive-empty">Arşivde öğe yok.</div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {archivedRows.map(a => (
              <div key={a.id} className="zen-panel p-3 flex flex-col items-center text-center group relative opacity-70 hover:opacity-100 transition-opacity" data-testid={`kartela-archive-card-${a.code || a.id}`}>
                {a.imageBase64 ? (
                  <img src={a.imageBase64} alt={a.label} className="w-20 h-20 object-cover rounded-lg border border-slate-200 dark:border-[#22304A] grayscale" />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-slate-100 dark:bg-[#22304A] flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-slate-300" />
                  </div>
                )}
                <div className="text-xs font-bold mt-2 truncate w-full text-slate-500">{a.label}</div>
                {a.code && <div className="text-[10px] text-slate-400 zen-mono">{a.code}</div>}
                {a.deletedAt && <div className="text-[9px] text-slate-400 mt-0.5">{new Date(a.deletedAt).toLocaleDateString("tr-TR")}</div>}
                <button onClick={() => restore(a)} data-testid={`kartela-restore-${a.code || a.id}`}
                  className="mt-2 w-full text-[10px] font-bold px-2 py-1 rounded bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors flex items-center justify-center gap-1">
                  <RefreshCw className="w-3 h-3" /> Geri Getir
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Yardımcı: Canvas ile görsel yeniden boyutlandırma ─────────────────────
function resizeImageToBase64(file, maxSize = 400, quality = 0.88) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const s = Math.min(maxSize, Math.max(img.width, img.height));
        const ratio = s / Math.max(img.width, img.height);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const cv = document.createElement("canvas");
        cv.width = w; cv.height = h;
        cv.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(cv.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// ─── Toplu Görsel Yükleme Modal ─────────────────────────────────────────────
function BulkPhotoUpload({ rows, onClose, onSaved }) {
  const [matches, setMatches] = useState([]);   // [{file, asset, b64, include}]
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(0);
  const fileInputRef = useRef(null);

  // Dosya adını kumaş koduyla eşleştir
  const matchFileToAsset = (filename, assets) => {
    const base = filename.replace(/\.[^.]+$/, "").trim();
    return assets.find(a => {
      const code = (a.code || "").trim();
      if (!code) return false;
      if (code === base) return true;
      if (code.toLowerCase() === base.toLowerCase()) return true;
      if (code.replace(/[-_\s]/g,"") === base.replace(/[-_\s]/g,"")) return true;
      if (base.toLowerCase().includes(code.toLowerCase())) return true;
      return false;
    }) || null;
  };

  const handleFiles = async (files) => {
    const fileList = Array.from(files);
    const results = await Promise.all(fileList.map(async (file) => {
      const asset = matchFileToAsset(file.name, rows);
      const b64   = await resizeImageToBase64(file, 400, 0.88);
      return { file, asset, b64, include: !!asset };
    }));
    setMatches(results);
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handleSave = async () => {
    setSaving(true);
    let count = 0;
    for (const m of matches) {
      if (!m.include || !m.asset) continue;
      try {
        await api.put(`/assets/${m.asset.id}`, { ...m.asset, imageBase64: m.b64 });
        count++;
        setSaved(count);
      } catch { /* devam et */ }
    }
    setSaving(false);
    toast.success(`${count} kumaş fotoğrafı güncellendi`);
    onSaved();
    onClose();
  };

  const matched   = matches.filter(m => m.asset);
  const unmatched = matches.filter(m => !m.asset);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="zen-panel w-full max-w-2xl p-6 zen-anim-up max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="bulk-upload-modal">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-bold">Toplu Fotoğraf Yükle</h3>
            <p className="text-xs text-slate-500 mt-0.5">Dosya adı = kumaş kodu olmalı. Örn: <span className="zen-mono font-semibold">92-50690.jpg</span></p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {/* Dosya seçim alanı */}
        {matches.length === 0 && (
          <div
            onDrop={onDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-slate-300 dark:border-[#22304A] rounded-xl p-10 text-center cursor-pointer hover:border-[#E5322A] hover:bg-rose-50/5 transition-colors"
            data-testid="bulk-dropzone"
          >
            <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">Fotoğrafları buraya sürükleyin</p>
            <p className="text-xs text-slate-400 mt-1">veya tıklayarak seçin — JPG, PNG, WEBP</p>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
              onChange={e => handleFiles(e.target.files)} />
          </div>
        )}

        {/* Eşleştirme sonuçları */}
        {matches.length > 0 && (
          <>
            <div className="flex gap-3 mb-3 text-xs font-semibold">
              <span className="text-emerald-600">{matched.length} eşleşti</span>
              {unmatched.length > 0 && <span className="text-amber-500">{unmatched.length} eşleşmedi</span>}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4 max-h-72 overflow-y-auto pr-1">
              {matches.map((m, i) => (
                <div key={i}
                  className={`relative rounded-lg border p-2 flex flex-col items-center gap-1 cursor-pointer transition-all ${m.include ? "border-emerald-400 dark:border-emerald-600 bg-emerald-50/10" : "border-slate-200 dark:border-[#22304A] opacity-50"}`}
                  onClick={() => setMatches(prev => prev.map((x,j) => j===i ? {...x, include: !x.include} : x))}
                >
                  <img src={m.b64} alt="" className="w-16 h-16 object-cover rounded" />
                  {m.asset ? (
                    <div className="text-center w-full">
                      <div className="text-[10px] font-bold truncate">{m.asset.code}</div>
                      <div className="text-[9px] text-slate-500 truncate">{m.asset.label}</div>
                    </div>
                  ) : (
                    <div className="text-[10px] text-amber-500 font-semibold truncate w-full text-center">{m.file.name}</div>
                  )}
                  {m.include && m.asset && <div className="absolute top-1 right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center"><CheckCircle2 className="w-3 h-3 text-white" /></div>}
                  {!m.asset && <div className="absolute top-1 right-1 text-[9px] bg-amber-400 text-white rounded px-1">?</div>}
                </div>
              ))}
            </div>

            <div className="text-xs text-slate-500 mb-4">Eşleşmeyen görseller atlanır. Tıklayarak dahil etme / çıkarma yapabilirsiniz.</div>

            <div className="flex gap-2 justify-end">
              <button onClick={() => setMatches([])} className="zen-btn-ghost text-xs">Yeniden Seç</button>
              <button
                onClick={handleSave}
                disabled={saving || matched.filter(m=>m.include).length === 0}
                className="zen-btn-primary text-xs"
                data-testid="bulk-save-btn"
              >
                {saving ? `Kaydediliyor… ${saved}/${matched.filter(m=>m.include).length}` : `${matched.filter(m=>m.include).length} Fotoğrafı Kaydet`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function KartelaLightbox({ asset, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const isPerf = (asset.label || "").toLowerCase().includes("perfore");
  const perfStyle = (hex) => {
    if (!hex || !isPerf) return {};
    const lum = parseInt((hex || "#888").slice(1), 16);
    const hole = lum < 0x888888 ? "rgba(255,255,255,0.30)" : "rgba(0,0,0,0.28)";
    return {
      backgroundImage: `radial-gradient(circle, ${hole} 35%, transparent 37%)`,
      backgroundSize: "8px 8px",
    };
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="kartela-lightbox"
    >
      <div
        className="relative bg-white dark:bg-[#131B2E] rounded-2xl shadow-2xl p-4 max-w-sm w-full mx-4 flex flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Kapat butonu */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 dark:bg-[#22304A] text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
          data-testid="kartela-lightbox-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Görsel */}
        {(asset._imgSrc || asset.imageBase64) ? (
          <img
            src={asset._imgSrc || asset.imageBase64}
            alt={asset.label}
            className="w-80 rounded-xl border border-slate-200 dark:border-[#22304A] shadow-lg object-cover"
            style={{ maxHeight: "22rem", imageRendering: "auto", objectFit: "cover" }}
          />
        ) : asset.hexColor ? (
          <div
            className="w-80 h-80 rounded-xl border border-slate-200 dark:border-[#22304A] shadow-lg"
            style={{ background: asset.hexColor, ...perfStyle(asset.hexColor) }}
          />
        ) : (
          <div className="w-80 h-80 rounded-xl bg-slate-100 dark:bg-[#22304A] flex items-center justify-center text-slate-400">Görsel Yok</div>
        )}

        {/* Bilgi */}
        <div className="text-center w-full">
          <div className="text-sm font-bold text-slate-800 dark:text-white leading-snug">{asset.label}</div>
          {asset.code && <div className="text-xs text-slate-500 dark:text-slate-400 zen-mono mt-0.5">{asset.code}</div>}
          {asset.hexColor && <div className="text-[11px] text-slate-400 font-mono mt-0.5">{asset.hexColor}</div>}
          {isPerf && <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-[#22304A] text-[10px] text-slate-500"><span>⊙</span> Perfore (Delikli)</div>}
          {asset.brand && <div className="text-[11px] text-[#E5322A] font-semibold mt-1">{asset.brand}</div>}
          {asset.price > 0 && <div className="text-xs font-semibold text-[#E5322A] mt-1">{asset.price} {asset.currency}</div>}
        </div>

        <div className="text-[10px] text-slate-400 mt-1">Kapatmak için dışarı tıklayın veya Esc</div>
      </div>
    </div>
  );
}

function KartelaForm({ kind, data, brands = [], onClose, onSave, stockEnabled }) {
  const [f, setF] = useState({ label: "", code: "", imageBase64: "", hexColor: "", group: "", brand: "", price: 0, currency: "TL", purchasePrice: 0, purchaseCurrency: "TL", outOfStock: false, stockNote: "", currentStock: 0, groupSlugs: [], fabricWidthCm: 300, fabricCategory: "", ...data });
  const [productGroups, setProductGroups] = useState([]);
  const [imgSearch, setImgSearch] = useState({ open: false, q: "", results: [], loading: false });
  useEffect(() => {
    if (kind === "fabric" || kind === "motor") {
      api.get("/product-groups").then(r => setProductGroups(r.data || [])).catch(() => {});
    }
  }, [kind]);
  const handleFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    resizeImageToBase64(file, 400, 0.88).then(b64 => setF({ ...f, imageBase64: b64 }));
  };
  const searchImages = async () => {
    const q = imgSearch.q.trim();
    if (!q) { toast.error("Arama terimi girin"); return; }
    setImgSearch(s => ({ ...s, loading: true, results: [] }));
    try {
      const { data: res } = await api.get(`/images/search?q=${encodeURIComponent(q)}&num=8`);
      setImgSearch(s => ({ ...s, loading: false, results: res.results || [] }));
      if (!res.results?.length) toast.info("Sonuç bulunamadı");
    } catch (e) {
      setImgSearch(s => ({ ...s, loading: false }));
      toast.error(e?.response?.data?.detail || "Görsel arama başarısız");
    }
  };
  const fetchAndSetImage = async (url) => {
    try {
      toast.loading("Görsel yükleniyor...", { id: "fetch-img" });
      const { data: res } = await api.get(`/images/fetch-url?url=${encodeURIComponent(url)}`);
      setF(prev => ({ ...prev, imageBase64: res.base64 }));
      setImgSearch(s => ({ ...s, open: false }));
      toast.success("Görsel seçildi", { id: "fetch-img" });
    } catch (e) {
      toast.error("Görsel alınamadı", { id: "fetch-img" });
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="zen-panel w-full max-w-md p-6 zen-anim-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">{data?.id ? "Kartela Düzenle" : "Yeni Kartela"} · {KARTELA_KINDS.find(k => k.key === kind)?.label}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 text-sm">
          {f.imageBase64 && <img src={f.imageBase64} alt="" className="w-24 h-24 rounded-lg object-cover border border-slate-200 dark:border-[#22304A] mx-auto" />}
          <div>
            <label className="block text-xs font-semibold mb-1">Görsel (opsiyonel, max 500KB)</label>
            <div className="flex gap-2">
              <input type="file" accept="image/*" onChange={handleFile} className="zen-input !py-1.5 flex-1" data-testid="kartela-img" />
              <button type="button"
                onClick={() => setImgSearch(s => ({ ...s, open: !s.open, q: s.q || f.label || f.code || "" }))}
                className="zen-btn-ghost !px-3 text-xs whitespace-nowrap flex items-center gap-1.5 shrink-0"
                data-testid="kartela-img-search-btn">
                <Search className="w-3.5 h-3.5" /> Görsel Ara
              </button>
            </div>
            {imgSearch.open && (
              <div className="mt-2 p-3 bg-slate-50 dark:bg-[#0F1826] rounded-lg border border-slate-200 dark:border-[#22304A] space-y-2">
                <div className="flex gap-2">
                  <input className="zen-input flex-1 !py-1.5 text-xs" value={imgSearch.q}
                    onChange={e => setImgSearch(s => ({ ...s, q: e.target.value }))}
                    onKeyDown={e => e.key === "Enter" && searchImages()}
                    placeholder="Arama terimi (örn: blackout fabric)..."
                    data-testid="kartela-img-search-input" />
                  <button onClick={searchImages} disabled={imgSearch.loading}
                    className="zen-btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5 shrink-0"
                    data-testid="kartela-img-search-go">
                    {imgSearch.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Ara
                  </button>
                </div>
                {imgSearch.results.length > 0 && (
                  <div className="grid grid-cols-4 gap-1.5" data-testid="kartela-img-search-results">
                    {imgSearch.results.map((r, i) => (
                      <button key={i} onClick={() => fetchAndSetImage(r.link)} title={r.title}
                        className="aspect-square overflow-hidden rounded border border-slate-200 dark:border-[#22304A] hover:border-[#E5322A] hover:shadow-md transition-all"
                        data-testid={`kartela-img-result-${i}`}>
                        <img src={r.thumbnail} alt={r.title} className="w-full h-full object-cover"
                          onError={e => { e.currentTarget.parentElement.style.opacity = "0.3"; }} />
                      </button>
                    ))}
                  </div>
                )}
                {imgSearch.results.length === 0 && !imgSearch.loading && (
                  <p className="text-[11px] text-slate-400 text-center py-1">Yukarıda arama yapın</p>
                )}
              </div>
            )}
          </div>
          <div><label className="block text-xs font-semibold mb-1">İsim / Etiket</label>
            <input className="zen-input" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="kartela-label" />
          </div>
          <div><label className="block text-xs font-semibold mb-1">Kod (opsiyonel)</label>
            <input className="zen-input" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder={kind === "ral" ? "RAL 9016" : kind === "fabric" ? "3710" : kind === "metal_slat" ? "P1" : ""} data-testid="kartela-code" />
          </div>
          {kind === "fabric" && (
            <div><label className="block text-xs font-semibold mb-1">Kod Sonu (renk aralığı)</label>
              <input className="zen-input" value={f.codeEnd || ""} onChange={(e) => setF({ ...f, codeEnd: e.target.value })} placeholder="örn. 3739 (boş = tek kod)" data-testid="kartela-code-end" />
            </div>
          )}
          {kind === "fabric" && (
            <div className="grid grid-cols-2 gap-2">
              <div><label className="block text-xs font-semibold mb-1">Alış Fiyatı (m²)</label>
                <input type="number" className="zen-input" value={f.purchasePrice || 0} onChange={(e) => setF({ ...f, purchasePrice: Number(e.target.value) })} data-testid="kartela-purchase-price" />
              </div>
              <div><label className="block text-xs font-semibold mb-1">Alış Para Birimi</label>
                <select className="zen-input" value={f.purchaseCurrency || "TL"} onChange={(e) => setF({ ...f, purchaseCurrency: e.target.value })} data-testid="kartela-purchase-currency">
                  <option>TL</option><option>EUR</option><option>USD</option>
                </select>
              </div>
            </div>
          )}
          {kind === "fabric" && stockEnabled && (
            <div><label className="block text-xs font-semibold mb-1">Stok (metre) <span className="text-slate-400">— sipariş onaylandığında otomatik düşer</span></label>
              <input type="number" step="0.1" className="zen-input" value={f.currentStock ?? 0} onChange={(e) => setF({ ...f, currentStock: Number(e.target.value) })} data-testid="kartela-stock-qty" />
            </div>
          )}
          {kind === "fabric" && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                <input type="checkbox" checked={!!f.outOfStock} onChange={(e) => setF({ ...f, outOfStock: e.target.checked })} data-testid="kartela-out-of-stock" />
                Stok dışı (sipariş formunda uyarı verir)
              </label>
              <input className="zen-input !py-1.5" placeholder="Stok notu (örn. 3931-3935 stok dışı)" value={f.stockNote || ""} onChange={(e) => setF({ ...f, stockNote: e.target.value })} data-testid="kartela-stock-note" />
            </div>
          )}
          {kind === "fabric" && (
            <div><label className="block text-xs font-semibold mb-1">Marka / Tedarikçi <span className="text-slate-400">(sadece iç kullanım)</span></label>
              <input className="zen-input" list="kartela-brand-list" value={f.brand || ""} onChange={(e) => setF({ ...f, brand: e.target.value })}
                placeholder="örn. Vatan Perde — yeni marka da yazabilirsiniz" data-testid="kartela-brand-input" />
              <datalist id="kartela-brand-list">
                {brands.map((b) => <option key={b} value={b} />)}
              </datalist>
              <div className="text-[10px] text-slate-500 mt-1">Teklif/PDF çıktılarında görünmez. Listeden seçebilir veya yeni marka adı yazabilirsiniz.</div>
            </div>
          )}
          {(kind === "motor" || kind === "control" || kind === "wood_type" || kind === "fabric") && (
            <div className="grid grid-cols-2 gap-2">
              <div><label className="block text-xs font-semibold mb-1">Fiyat</label>
                <input type="number" className="zen-input" value={f.price} onChange={(e) => setF({ ...f, price: Number(e.target.value) })} />
              </div>
              <div><label className="block text-xs font-semibold mb-1">Para Birimi</label>
                <select className="zen-input" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
                  <option>TL</option><option>EUR</option><option>USD</option>
                </select>
              </div>
            </div>
          )}
          {/* TÜL & FON: Kumaş Eni + Kategori */}
          {kind === "fabric" && (f.groupSlugs || []).includes("tul_fon_perde") && (
            <>
              <div>
                <label className="block text-xs font-semibold mb-1">Kumaş Eni <span className="text-slate-400 font-normal">(cm) — döşemelik için 140 girin</span></label>
                <input type="number" className="zen-input" value={f.fabricWidthCm || ""} placeholder="örn. 300"
                  onChange={e => setF(p => ({ ...p, fabricWidthCm: Number(e.target.value) }))}
                  data-testid="kartela-fabric-width" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-2">Kumaş Kategorisi <span className="text-slate-400 font-normal">(dropdown gruplamada kullanılır)</span></label>
                <div className="flex gap-2">
                  {[
                    { val: "tf_tul",    label: "Tül",        defaultEni: 300 },
                    { val: "tf_fon",    label: "Fon Perde",  defaultEni: 300 },
                    { val: "dosemelik", label: "Döşemelik",  defaultEni: 140 },
                  ].map(opt => (
                    <button
                      key={opt.val}
                      type="button"
                      data-testid={`kartela-cat-${opt.val}`}
                      onClick={() => setF(p => ({
                        ...p, group: opt.val,
                        fabricWidthCm: p.fabricWidthCm || opt.defaultEni,
                      }))}
                      className={`flex-1 py-1.5 text-xs font-semibold rounded border transition-colors ${
                        f.group === opt.val
                          ? "bg-[#E5322A] text-white border-[#E5322A]"
                          : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:border-[#E5322A]/60"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {!["tf_tul","tf_fon","dosemelik"].includes(f.group) && (
                  <p className="text-[10px] text-amber-500 mt-1">Henüz kategori seçilmedi — dropdown'da "Fon Perdeler" altında görünecek</p>
                )}
              </div>
            </>
          )}
          {kind === "fabric" && (
            <div>
              <label className="block text-xs font-semibold mb-1">Kategori / Kumaş Serisi <span className="text-slate-400 font-normal">(hiyerarşide nerede görünsün)</span></label>
              <select className="zen-input" value={f.group || ""} onChange={(e) => setF({ ...f, group: e.target.value })} data-testid="kartela-group-select">
                <option value="">Genel (kategori seçilmedi)</option>
                <optgroup label="─ Zip Perde ─">
                  <option value="zip_kumas_86">Zip — Serge Ferrari 86</option>
                  <option value="zip_kumas_92">Zip — Serge Ferrari 92</option>
                  <option value="zip_kumas_96">Zip — Serge Ferrari 96</option>
                  <option value="zip_kumas_helio">Zip — Helioscreen</option>
                  <option value="zip_kumas_akrilik">Zip — Akrilik</option>
                </optgroup>
                <optgroup label="─ Stor Perde ─">
                  <option value="stor">Stor (Genel)</option>
                  {STOR_KUMAS_GROUPS.map(g => <option key={g} value={g}>Stor — {FABRIC_GROUP_LABELS[g] || g}</option>)}
                </optgroup>
                <optgroup label="─ Plise / Honeycomb ─">
                  <option value="plise14">PLİCELL 15mm</option>
                  <option value="honeycomb_16">Honeycomb 16mm</option>
                  <option value="plise25">PLİCELL 20mm</option>
                  <option value="honeycomb_20">Honeycomb 20mm</option>
                  <option value="honeycomb_26">Honeycomb 26mm</option>
                  <option value="natty_plise">Natty Plise</option>
                </optgroup>
                <optgroup label="─ Diğer ─">
                  <option value="silhouette">Silhouette</option>
                  <option value="ciftli_sistem">Çiftli Sistem</option>
                  <option value="dikey_kumas">Dikey Kumaş</option>
                  <option value="dikey_pvc">Dikey PVC</option>
                  <option value="dikey_tul">Dikey Tül</option>
                </optgroup>
              </select>
              <div className="text-[10px] text-slate-500 mt-1">Kumaşın hiyerarşi sayfasındaki konumunu belirler.</div>
            </div>
          )}
          {kind === "motor" && (
            <div>
              <label className="block text-xs font-semibold mb-1">Seri / Grup Kodu</label>
              <input className="zen-input" value={f.group || ""} onChange={(e) => setF({ ...f, group: e.target.value })} placeholder="Örn: somfy_wt_45" data-testid="kartela-motor-group" />
              <div className="text-[10px] text-slate-400 mt-1">Hiyerarşideki seri konumunu belirler (otomatik doldurulur)</div>
            </div>
          )}
          {(kind === "fabric" || kind === "motor") && productGroups.length > 0 && (
            <div>
              <label className="block text-xs font-semibold mb-1.5">
                {kind === "motor" ? "Hangi Ürün Formlarında Kullanılsın?" : "Hangi Ürün Formlarında Görünsün?"}
                <span className="text-slate-400 font-normal ml-1">(boş bırakılırsa bağlantı mantığı otomatik çalışır)</span>
              </label>
              <div className="flex flex-wrap gap-1.5" data-testid="kartela-group-slugs-wrap">
                {productGroups
                  .filter(pg => !MOTOR_FORM_SLUGS.includes(pg.slug) && pg.active !== false)
                  .map(pg => {
                    const checked = (f.groupSlugs || []).includes(pg.slug);
                    return (
                      <button
                        key={pg.slug}
                        type="button"
                        onClick={() => {
                          const current = f.groupSlugs || [];
                          setF(prev => ({
                            ...prev,
                            groupSlugs: checked
                              ? current.filter(s => s !== pg.slug)
                              : [...current, pg.slug],
                          }));
                        }}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                          checked
                            ? kind === "motor"
                              ? "bg-amber-500 text-white border-amber-500 shadow-sm"
                              : "bg-[#E5322A] text-white border-[#E5322A] shadow-sm"
                            : "border-slate-200 dark:border-[#22304A] text-slate-500 hover:bg-slate-50 dark:hover:bg-[#0C1220] hover:border-slate-300"
                        }`}
                        data-testid={`kartela-group-chip-${pg.slug}`}
                      >
                        {checked && <Check className="w-3 h-3 shrink-0" />}
                        {pg.name}
                      </button>
                    );
                  })}
              </div>
              {(f.groupSlugs || []).length === 0 ? (
                <div className="text-[10px] text-slate-400 mt-1.5">
                  {kind === "motor" ? "Seçim yapılmadı — motor tüm motorlu formlarda görünür" : "Seçim yapılmadı — form-kumaş bağlantı tablosu ve varsayılan mantık aktif"}
                </div>
              ) : (
                <div className={`text-[10px] mt-1.5 font-semibold ${kind === "motor" ? "text-amber-500" : "text-emerald-500"}`}>
                  {(f.groupSlugs || []).length} form seçildi — yalnızca bu formlarda görünür (öncelikli)
                </div>
              )}
            </div>
          )}
          {kind === "wood_type" && (
            <div><label className="block text-xs font-semibold mb-1">Kalınlık Kategorisi</label>
              <select className="zen-input" value={f.group || ""} onChange={(e) => setF({ ...f, group: e.target.value })} data-testid="kartela-group-select">
                <option value="">Seçilmedi (Diğer)</option>
                {KARTELA_CATEGORY_MAP.wood_type.filter(c => c.groups).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          )}
          {kind === "wood" && (
            <div><label className="block text-xs font-semibold mb-1">Kalınlık Kategorisi</label>
              <select className="zen-input" value={f.group || ""} onChange={(e) => setF({ ...f, group: e.target.value })} data-testid="kartela-group-select">
                <option value="">Seçilmedi (Diğer)</option>
                {KARTELA_CATEGORY_MAP.wood.filter(c => c.groups).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          )}
          {kind === "metal_slat" && (
            <div>
              <label className="block text-xs font-semibold mb-1">Renk Kodu (Hex) <span className="text-slate-400">— önizleme için</span></label>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded border border-slate-300 dark:border-[#31405D] shrink-0 shadow-sm" style={{ background: f.hexColor || "#CCCCCC" }} />
                <input
                  type="color"
                  className="w-9 h-9 rounded border border-slate-200 dark:border-[#31405D] cursor-pointer p-0.5 bg-transparent"
                  value={f.hexColor || "#CCCCCC"}
                  onChange={(e) => setF({ ...f, hexColor: e.target.value })}
                  data-testid="kartela-hex-color-picker"
                />
                <input
                  className="zen-input flex-1"
                  value={f.hexColor || ""}
                  onChange={(e) => setF({ ...f, hexColor: e.target.value })}
                  placeholder="#FFFFFF"
                  data-testid="kartela-hex-color-input"
                />
              </div>
            </div>
          )}
          {kind === "metal_slat" && (
            <div><label className="block text-xs font-semibold mb-1">Kalınlık Kategorisi</label>
              <select className="zen-input" value={f.group || ""} onChange={(e) => setF({ ...f, group: e.target.value })} data-testid="kartela-group-select">
                <option value="">Seçilmedi (Diğer)</option>
                {KARTELA_CATEGORY_MAP.metal_slat.filter(c => c.groups).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="zen-btn-ghost">Vazgeç</button>
          <button onClick={() => onSave(f)} className="zen-btn-primary" data-testid="kartela-save"><CheckCircle2 className="w-4 h-4" /> Kaydet</button>
        </div>
      </div>
    </div>
  );
}

// ============ BOT & HABERLEŞME MERKEZİ ============

export { KartelaModule };
export default KartelaModule;
